import api from './api';
import { mutate } from 'swr';

// Helper function to trigger SWR revalidation
export const revalidate = (key: string | string[]) => {
  mutate(key);
};

/** Revalidate all SWR keys that start with a prefix. */
export const revalidatePrefix = (prefix: string) => {
  mutate((key) => typeof key === 'string' && key.startsWith(prefix));
};

// Documents
const revalidateDocuments = (id?: number) => {
  if (id) mutate(`/documents/${id}`);
  revalidatePrefix('/documents?');
  mutate('/documents');
};
export const createDocument = async (data: FormData) => {
  // api's interceptor removes JSON Content-Type for FormData so the browser supplies its boundary.
  const response = await api.post('/documents', data);
  revalidateDocuments();
  return response.data;
};
export const bulkImportDocuments = async (data: FormData) => {
  const response = await api.post('/documents/bulk', data);
  revalidateDocuments();
  return response.data as {
    created: Array<{ id: number; title: string }>;
    errors: Array<{ filename: string; message: string }>;
  };
};
export const updateDocument = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/documents/${id}`, data);
  revalidateDocuments(id);
  return response.data;
};
export const deleteDocument = async (id: number) => {
  await api.delete(`/documents/${id}`);
  revalidateDocuments(id);
};
export const scanDocument = async (id: number) => {
  const response = await api.post(`/documents/${id}/scan`);
  revalidateDocuments(id);
  return response.data;
};
export const applyDocumentPostingRules = async (id: number) => {
  const response = await api.post(`/documents/${id}/apply-posting-rules`);
  revalidateDocuments(id);
  return response.data;
};
export const teachDocumentPosting = async (id: number, data: Record<string, unknown>) => {
  const response = await api.post(`/documents/${id}/teach-posting`, data);
  revalidateDocuments(id);
  mutate('/document-posting-rules');
  return response.data;
};
export const submitDocumentForApproval = async (id: number) => {
  const response = await api.post(`/documents/${id}/submit-for-approval`);
  revalidateDocuments(id);
  revalidateApprovals();
  return response.data;
};
export const createDocumentPostingRule = async (data: Record<string, unknown>) => {
  const response = await api.post('/document-posting-rules', data);
  mutate('/document-posting-rules');
  return response.data;
};
export const updateDocumentPostingRule = async (id: number, data: Record<string, unknown>) => {
  const response = await api.put(`/document-posting-rules/${id}`, data);
  mutate('/document-posting-rules');
  return response.data;
};
export const deleteDocumentPostingRule = async (id: number) => {
  await api.delete(`/document-posting-rules/${id}`);
  mutate('/document-posting-rules');
};

// Approvals
const revalidateApprovals = (id?: number) => {
  if (id) void mutate(`/approvals/${id}`);
  void mutate(
    (key) =>
      typeof key === 'string' &&
      (key === '/approvals' ||
        key.startsWith('/approvals?') ||
        key.startsWith('/approvals/')),
  );
};
const revalidateEFormSubmissions = (id?: number) => {
  if (id) void mutate(`/e-form-submissions/${id}`);
  void mutate(
    (key) =>
      typeof key === 'string' &&
      (key === '/e-form-submissions' ||
        key.startsWith('/e-form-submissions?') ||
        key.startsWith('/e-form-submissions/')),
  );
};
/** Approvals list/detail plus linked e-form submission rows that show approval status. */
const revalidateApprovalSideEffects = (approvalId?: number) => {
  revalidateApprovals(approvalId);
  revalidateEFormSubmissions();
};
export const createApproval = async (data: Record<string, unknown>) => {
  const response = await api.post('/approvals', data);
  revalidateApprovalSideEffects();
  return response.data;
};
export const updateApproval = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/approvals/${id}`, data);
  revalidateApprovalSideEffects(id);
  return response.data;
};
export const deleteApproval = async (id: number) => {
  await api.delete(`/approvals/${id}`);
  revalidateApprovalSideEffects(id);
};
export const submitApproval = async (id: number, comments?: string) => {
  const response = await api.post(`/approvals/${id}/submit`, { comments });
  revalidateApprovalSideEffects(id);
  return response.data;
};
export const approveApprovalStep = async (id: number, stepId: number, comments?: string) => {
  const response = await api.post(`/approvals/${id}/steps/${stepId}/approve`, { comments });
  revalidateApprovalSideEffects(id);
  return response.data;
};
export const rejectApprovalStep = async (id: number, stepId: number, comments?: string) => {
  const response = await api.post(`/approvals/${id}/steps/${stepId}/reject`, { comments });
  revalidateApprovalSideEffects(id);
  return response.data;
};
export const cancelApproval = async (id: number) => {
  const response = await api.post(`/approvals/${id}/cancel`);
  revalidateApprovalSideEffects(id);
  return response.data;
};
export const createApprovalTemplate = async (data: Record<string, unknown>) => {
  const response = await api.post('/approval-templates', data);
  mutate('/approval-templates');
  return response.data;
};
export const updateApprovalTemplate = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/approval-templates/${id}`, data);
  mutate('/approval-templates');
  return response.data;
};
export const deleteApprovalTemplate = async (id: number) => {
  await api.delete(`/approval-templates/${id}`);
  mutate('/approval-templates');
};

// Approval delegations ("act on behalf of")
const revalidateApprovalDelegations = () => mutate('/approvals/delegations');
export const createApprovalDelegation = async (data: Record<string, unknown>) => {
  const response = await api.post('/approvals/delegations', data);
  revalidateApprovalDelegations();
  return response.data;
};
export const updateApprovalDelegation = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/approvals/delegations/${id}`, data);
  revalidateApprovalDelegations();
  return response.data;
};
export const deleteApprovalDelegation = async (id: number) => {
  await api.delete(`/approvals/delegations/${id}`);
  revalidateApprovalDelegations();
};

// Client mutations
export const createClient = async (data: any, files?: File[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const response = await api.post('/crm-clients', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate('/crm-clients'); // Revalidate clients list
  return response.data;
};

export const updateClient = async (id: number, data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  if (filesToDetach && filesToDetach.length > 0) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }

  const response = await api.patch(`/crm-clients/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate(`/crm-clients/${id}`); // Revalidate specific client
  mutate('/crm-clients'); // Revalidate clients list
  return response.data;
};

export const deleteClient = async (id: number) => {
  await api.delete(`/crm-clients/${id}`);
  mutate('/crm-clients'); // Revalidate clients list
};

// Marketing client mutations (uses /crm-clients/marketing endpoint for create)
export const createMarketingClient = async (data: any) => {
  // Marketing endpoint doesn't use FormData, just JSON
  const response = await api.post('/crm-clients/marketing', data);
  mutate('/crm-clients/marketing'); // Revalidate marketing list
  mutate('/crm-clients'); // Revalidate clients list
  return response.data;
};

// Reminder mutations
export const createReminder = async (data: any) => {
  const response = await api.post('/crm-clients-reminders', data);
  mutate('/crm-clients-reminders'); // Revalidate reminders list
  return response.data;
};

export const updateReminder = async (id: number, data: any) => {
  const response = await api.patch(`/crm-clients-reminders/${id}`, data);
  mutate(`/crm-clients-reminders/${id}`); // Revalidate specific reminder
  mutate('/crm-clients-reminders'); // Revalidate reminders list
  return response.data;
};

export const deleteReminder = async (id: number) => {
  await api.delete(`/crm-clients-reminders/${id}`);
  mutate(`/crm-clients-reminders/${id}`); // Revalidate specific reminder
  mutate('/crm-clients-reminders'); // Revalidate reminders list
};

// Task mutations
export const createTask = async (data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const response = await api.post('/crm-tasks', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate('/crm-tasks'); // Revalidate tasks list
  mutate('/dashboard/stats'); // Revalidate dashboard stats
  return response.data;
};

export const updateTask = async (id: number, data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  if (filesToDetach && filesToDetach.length > 0) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }

  const response = await api.patch(`/crm-tasks/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate(`/crm-tasks/${id}`); // Revalidate specific task
  mutate('/crm-tasks'); // Revalidate tasks list
  mutate('/dashboard/stats'); // Revalidate dashboard stats
  return response.data;
};

export const deleteTask = async (id: number) => {
  await api.delete(`/crm-tasks/${id}`);
  mutate('/crm-tasks'); // Revalidate tasks list
  mutate('/dashboard/stats'); // Revalidate dashboard stats
};

// Support Ticket mutations
export const createSupportTicket = async (data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  if (filesToDetach?.length) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }
  if (files?.length) {
    files.forEach((file) => formData.append('files', file));
  }
  const response = await api.post('/support-ticket', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  mutate('/support-ticket');
  mutate('/dashboard/stats');
  return response.data;
};

export const updateSupportTicket = async (id: number, data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  if (filesToDetach?.length) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }
  if (files?.length) {
    files.forEach((file) => formData.append('files', file));
  }
  const response = await api.patch(`/support-ticket/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  mutate(`/support-ticket/${id}`);
  mutate('/support-ticket');
  mutate('/dashboard/stats');
  return response.data;
};

export const deleteSupportTicket = async (id: number) => {
  await api.delete(`/support-ticket/${id}`);
  mutate('/support-ticket'); // Revalidate tickets list
  mutate('/dashboard/stats'); // Revalidate dashboard stats
};

// Invoice mutations
export const createInvoice = async (data: any) => {
  const response = await api.post('/crm-clients-invoices', data);
  mutate('/crm-clients-invoices'); // Revalidate invoices list
  mutate('/dashboard/stats'); // Revalidate dashboard stats
  return response.data;
};

export const updateInvoice = async (id: number, data: any) => {
  const response = await api.put(`/crm-clients-invoices/${id}`, data);
  mutate(`/crm-clients-invoices/${id}`); // Revalidate specific invoice
  mutate('/crm-clients-invoices'); // Revalidate invoices list
  mutate('/dashboard/stats'); // Revalidate dashboard stats
  return response.data;
};

export const deleteInvoice = async (id: number) => {
  await api.delete(`/crm-clients-invoices/${id}`);
  mutate('/crm-clients-invoices'); // Revalidate invoices list
  mutate('/dashboard/stats'); // Revalidate dashboard stats
};
// Todo mutations
export const createTodo = async (data: any, files?: File[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const response = await api.post('/crm-todos', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate('/crm-todos'); // Revalidate list
  return response.data;
};

export const updateTodo = async (id: number, data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  if (filesToDetach?.length) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const response = await api.patch(`/crm-todos/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate(`/crm-todos/${id}`); // Revalidate specific item
  mutate('/crm-todos'); // Revalidate list
  return response.data;
};

export const deleteTodo = async (id: number) => {
  await api.delete(`/crm-todos/${id}`);
  mutate('/crm-todos'); // Revalidate list
};

// Appointment mutations
export const createAppointment = async (data: any, files?: File[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const response = await api.post('/crm-clients-appointments', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate('/crm-clients-appointments'); // Revalidate list
  return response.data;
};

export const updateAppointment = async (id: number, data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  if (filesToDetach && filesToDetach.length > 0) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }

  const response = await api.patch(`/crm-clients-appointments/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate(`/crm-clients-appointments/${id}`); // Revalidate specific item
  mutate('/crm-clients-appointments'); // Revalidate list
  return response.data;
};

export const deleteAppointment = async (id: number) => {
  await api.delete(`/crm-clients-appointments/${id}`);
  mutate('/crm-clients-appointments'); // Revalidate list
};

// Support Agreement mutations
export const createSupportAgreement = async (data: any) => {
  const response = await api.post('/crm-support-agreements', data);
  mutate('/crm-support-agreements'); // Revalidate list
  return response.data;
};

export const updateSupportAgreement = async (id: number, data: any) => {
  const response = await api.patch(`/crm-support-agreements/${id}`, data);
  mutate(`/crm-support-agreements/${id}`); // Revalidate specific item
  mutate('/crm-support-agreements'); // Revalidate list
  return response.data;
};

export const deleteSupportAgreement = async (id: number) => {
  await api.delete(`/crm-support-agreements/${id}`);
  mutate('/crm-support-agreements'); // Revalidate list
};

// Call mutations
export const createCall = async (data: any, files?: File[]) => {
  const response = await api.post('/crm-clients-calls', data);
  mutate('/crm-clients-calls'); // Revalidate calls list
  return response.data;
};

export const updateCall = async (id: number, data: any, files?: File[]) => {
  const response = await api.patch(`/crm-clients-calls/${id}`, data);
  mutate(`/crm-clients-calls/${id}`); // Revalidate specific call
  mutate('/crm-clients-calls'); // Revalidate calls list
  return response.data;
};

export const deleteCall = async (id: number) => {
  await api.delete(`/crm-clients-calls/${id}`);
  mutate(`/crm-clients-calls/${id}`); // Revalidate specific call
  mutate('/crm-clients-calls'); // Revalidate calls list
};

// Lead mutations
export const createLead = async (data: any, files?: File[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const response = await api.post('/crm-leads', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate('/crm-leads'); // Revalidate leads list
  return response.data;
};

export const updateLead = async (id: number, data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  if (filesToDetach && filesToDetach.length > 0) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }

  const response = await api.patch(`/crm-leads/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate(`/crm-leads/${id}`); // Revalidate specific lead
  mutate('/crm-leads'); // Revalidate leads list
  return response.data;
};

export const deleteLead = async (id: number) => {
  await api.delete(`/crm-leads/${id}`);
  mutate(`/crm-leads/${id}`); // Revalidate specific lead
  mutate('/crm-leads'); // Revalidate leads list
};

// Notes mutations
export const createCrmNote = async (data: any, files?: File[]) => {
  const response = await api.post('/crm-notes', data);
  mutate('/crm-notes'); // Revalidate notes list
  return response.data;
};

export const updateCrmNote = async (id: number, data: any, files?: File[]) => {
  const response = await api.patch(`/crm-notes/${id}`, data);
  mutate(`/crm-notes/${id}`); // Revalidate specific note
  mutate('/crm-notes'); // Revalidate notes list
  return response.data;
};

export const deleteCrmNote = async (id: number) => {
  await api.delete(`/crm-notes/${id}`);
  mutate(`/crm-notes/${id}`); // Revalidate specific note
  mutate('/crm-notes'); // Revalidate notes list
};

// Client Services mutations
export const createClientService = async (data: any, files?: File[]) => {
  const response = await api.post('/crm-clients-services', data);
  mutate('/crm-clients-services'); // Revalidate services list
  return response.data;
};

export const updateClientService = async (id: number, data: any, files?: File[]) => {
  const response = await api.patch(`/crm-clients-services/${id}`, data);
  mutate(`/crm-clients-services/${id}`); // Revalidate specific service
  mutate('/crm-clients-services'); // Revalidate services list
  return response.data;
};

export const deleteClientService = async (id: number) => {
  await api.delete(`/crm-clients-services/${id}`);
  mutate(`/crm-clients-services/${id}`); // Revalidate specific service
  mutate('/crm-clients-services'); // Revalidate services list
};

// Invoice Payment mutations
export const createInvoicePayment = async (data: any, files?: File[]) => {
  const response = await api.post('/crm-clients-invoice-payments', data);
  mutate('/crm-clients-invoice-payments'); // Revalidate payments list
  mutate('/crm-clients-invoices'); // Revalidate invoices list (payment status)
  return response.data;
};

export const updateInvoicePayment = async (id: number, data: any, files?: File[]) => {
  const response = await api.patch(`/crm-clients-invoice-payments/${id}`, data);
  mutate(`/crm-clients-invoice-payments/${id}`); // Revalidate specific item
  mutate('/crm-clients-invoice-payments'); // Revalidate list
  return response.data;
};

export const deleteInvoicePayment = async (id: number) => {
  await api.delete(`/crm-clients-invoice-payments/${id}`);
  mutate(`/crm-clients-invoice-payments/${id}`); // Revalidate specific item
  mutate('/crm-clients-invoice-payments'); // Revalidate list
};

// Client Invoice mutations
export const createClientInvoice = async (data: any, files?: File[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const response = await api.post('/crm-clients-invoices', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate('/crm-clients-invoices'); // Revalidate invoices list
  return response.data;
};

export const updateClientInvoice = async (id: number, data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  if (filesToDetach && filesToDetach.length > 0) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }

  const response = await api.patch(`/crm-clients-invoices/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate(`/crm-clients-invoices/${id}`); // Revalidate specific invoice
  mutate('/crm-clients-invoices'); // Revalidate invoices list
  return response.data;
};

export const deleteClientInvoice = async (id: number) => {
  await api.delete(`/crm-clients-invoices/${id}`);
  mutate(`/crm-clients-invoices/${id}`); // Revalidate specific invoice
  mutate('/crm-clients-invoices'); // Revalidate invoices list
};

// Supplier mutations
export const createSupplier = async (data: any) => {
  const response = await api.post('/suppliers', data);
  mutate('/suppliers');
  return response.data;
};

export const updateSupplier = async (id: number, data: any) => {
  const response = await api.patch(`/suppliers/${id}`, data);
  mutate(`/suppliers/${id}`);
  mutate('/suppliers');
  return response.data;
};

export const deleteSupplier = async (id: number) => {
  await api.delete(`/suppliers/${id}`);
  mutate('/suppliers');
};

export const createCrewFile = async (data: Record<string, unknown>) => {
  const response = await api.post('/crew', data);
  mutate('/crew');
  return response.data;
};

export const updateCrewFile = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/crew/${id}`, data);
  mutate(`/crew/${id}`);
  mutate('/crew');
  return response.data;
};

export const deleteCrewFile = async (id: number) => {
  await api.delete(`/crew/${id}`);
  mutate('/crew');
};

export const seedCrewReplacements = async (data: { vesselId?: number; withinDays?: number }) => {
  const response = await api.post('/crew/replacements/seed', data);
  return response.data as { scanned: number; created: number; revived: number; skipped: number };
};

export const createCrewReplacement = async (data: Record<string, unknown>) => {
  const response = await api.post('/crew/replacements', data);
  return response.data;
};

export const updateCrewReplacement = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/crew/replacements/${id}`, data);
  return response.data;
};

export const advanceCrewReplacement = async (id: number, status: string) => {
  const response = await api.post(`/crew/replacements/${id}/advance`, { status });
  return response.data;
};

export const completeCrewReplacement = async (id: number) => {
  const response = await api.post(`/crew/replacements/${id}/complete`);
  return response.data;
};

export const deleteCrewReplacement = async (id: number) => {
  await api.delete(`/crew/replacements/${id}`);
};

export const addCrewReplacementCandidate = async (
  replacementId: number,
  data: { crewId: number; notes?: string | null; contactedAt?: string | null },
) => {
  const response = await api.post(`/crew/replacements/${replacementId}/candidates`, data);
  return response.data;
};

/** Creates the slot if needed, then adds a possible candidate. */
export const addCrewReplacementCandidateForTour = async (data: {
  outgoingTransactionId: number;
  crewId: number;
  notes?: string | null;
}) => {
  const response = await api.post('/crew/replacements/candidates-for-tour', data);
  return response.data;
};

export const removeCrewReplacementCandidate = async (
  replacementId: number,
  candidateId: number,
) => {
  const response = await api.delete(
    `/crew/replacements/${replacementId}/candidates/${candidateId}`,
  );
  return response.data;
};

export const selectCrewReplacementCandidate = async (
  replacementId: number,
  candidateId: number,
) => {
  const response = await api.post(
    `/crew/replacements/${replacementId}/candidates/${candidateId}/select`,
  );
  return response.data;
};

export const createCrewTravelFromReplacement = async (replacementId: number) => {
  const response = await api.post(`/crew/travels/from-replacement/${replacementId}`);
  return response.data;
};

export const createCrewTravel = async (data: Record<string, unknown>) => {
  const response = await api.post('/crew/travels', data);
  return response.data;
};

export const updateCrewTravel = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/crew/travels/${id}`, data);
  return response.data;
};

export const deleteCrewTravel = async (id: number) => {
  await api.delete(`/crew/travels/${id}`);
};

export type CrewAiOverviewResponse = {
  id: number;
  crewId: number;
  generatedAt: string;
  model: string;
  overview: {
    headline: string;
    summary: string;
    readiness: 'READY' | 'CONDITIONAL' | 'NOT_READY' | 'UNKNOWN';
    strengths: string[];
    risks: string[];
    recommendations: string[];
  };
};

export const getCrewAiOverview = async (id: number): Promise<CrewAiOverviewResponse | null> => {
  const response = await api.get(`/crew/${id}/ai-overview`);
  return response.data ?? null;
};

export const getCrewAiOverviewHistory = async (id: number): Promise<CrewAiOverviewResponse[]> => {
  const response = await api.get(`/crew/${id}/ai-overviews`);
  return Array.isArray(response.data) ? response.data : [];
};

export const generateCrewAiOverview = async (id: number): Promise<CrewAiOverviewResponse> => {
  const response = await api.post(`/crew/${id}/ai-overview`);
  return response.data;
};

export type MgaAiOverviewResponse = {
  id: number;
  mgaPeriodId: number;
  generatedAt: string;
  model: string;
  overview: {
    headline: string;
    summary: string;
    readiness: 'READY' | 'CONDITIONAL' | 'NOT_READY' | 'UNKNOWN';
    strengths: string[];
    risks: string[];
    recommendations: string[];
  };
};

export const getMgaAiOverview = async (id: number): Promise<MgaAiOverviewResponse | null> => {
  const response = await api.get(`/mga/periods/${id}/ai-overview`);
  return response.data ?? null;
};

export const getMgaAiOverviewHistory = async (id: number): Promise<MgaAiOverviewResponse[]> => {
  const response = await api.get(`/mga/periods/${id}/ai-overviews`);
  return Array.isArray(response.data) ? response.data : [];
};

export const generateMgaAiOverview = async (id: number): Promise<MgaAiOverviewResponse> => {
  const response = await api.post(`/mga/periods/${id}/ai-overview`);
  return response.data;
};

// Coerce supplier invoice payload: id fields as numbers, empty strings -> null for optional FKs
const coerceSupplierInvoicePayload = (data: Record<string, unknown>) => {
  const out = { ...data };
  if (out.supplierId !== undefined && out.supplierId !== '' && out.supplierId !== null) {
    out.supplierId = Number(out.supplierId);
  }
  if (out.invoiceTypeId !== undefined && (out.invoiceTypeId === '' || out.invoiceTypeId === null)) {
    out.invoiceTypeId = null;
  } else if (out.invoiceTypeId !== undefined && out.invoiceTypeId !== '') {
    out.invoiceTypeId = Number(out.invoiceTypeId);
  }
  if (out.invoiceCategoryId !== undefined && (out.invoiceCategoryId === '' || out.invoiceCategoryId === null)) {
    out.invoiceCategoryId = null;
  } else if (out.invoiceCategoryId !== undefined && out.invoiceCategoryId !== '') {
    out.invoiceCategoryId = Number(out.invoiceCategoryId);
  }
  return out;
};

// Supplier Invoice mutations
export const createSupplierInvoice = async (data: any, files?: File[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(coerceSupplierInvoicePayload(data)));
  if (files?.length) {
    files.forEach((f) => formData.append('files', f));
  }
  const response = await api.post('/supplier-invoices', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  mutate('/supplier-invoices');
  return response.data;
};

export const updateSupplierInvoice = async (id: number, data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(coerceSupplierInvoicePayload(data)));
  if (files?.length) {
    files.forEach((f) => formData.append('files', f));
  }
  if (filesToDetach?.length) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }
  const response = await api.patch(`/supplier-invoices/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  mutate(`/supplier-invoices/${id}`);
  mutate('/supplier-invoices');
  return response.data;
};

export const deleteSupplierInvoice = async (id: number) => {
  await api.delete(`/supplier-invoices/${id}`);
  mutate(`/supplier-invoices/${id}`);
  mutate('/supplier-invoices');
};

// Supplier Invoice Payment mutations
export const createSupplierInvoicePayment = async (data: any) => {
  const response = await api.post('/supplier-invoice-payments', data);
  mutate('/supplier-invoice-payments');
  mutate('/supplier-invoices'); // Revalidate invoices list (payment status)
  return response.data;
};

export const updateSupplierInvoicePayment = async (id: number, data: any) => {
  const response = await api.patch(`/supplier-invoice-payments/${id}`, data);
  mutate(`/supplier-invoice-payments/${id}`);
  mutate('/supplier-invoice-payments');
  return response.data;
};

export const deleteSupplierInvoicePayment = async (id: number) => {
  await api.delete(`/supplier-invoice-payments/${id}`);
  mutate(`/supplier-invoice-payments/${id}`);
  mutate('/supplier-invoice-payments');
};

// Expense mutations
export const createExpense = async (data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  const response = await api.post('/crm-expense', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate('/crm-expense'); // Revalidate expenses list
  return response.data;
};

export const updateExpense = async (id: number, data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }

  if (filesToDetach && filesToDetach.length > 0) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }

  const response = await api.patch(`/crm-expense/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  mutate(`/crm-expense/${id}`); // Revalidate specific expense
  mutate('/crm-expense'); // Revalidate expenses list
  return response.data;
};

export const deleteExpense = async (id: number) => {
  await api.delete(`/crm-expense/${id}`);
  mutate(`/crm-expense/${id}`); // Revalidate specific expense
  mutate('/crm-expense'); // Revalidate expenses list
};

// Payables mutations
export const createPayable = async (data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  if (files?.length) {
    files.forEach((f) => formData.append('files', f));
  }
  const response = await api.post('/payables', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  mutate('/payables');
  return response.data;
};

export const updatePayable = async (id: number, data: any, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  if (files?.length) {
    files.forEach((f) => formData.append('files', f));
  }
  if (filesToDetach?.length) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }
  const response = await api.patch(`/payables/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  mutate(`/payables/${id}`);
  mutate('/payables');
  return response.data;
};

export const deletePayable = async (id: number) => {
  await api.delete(`/payables/${id}`);
  mutate(`/payables/${id}`);
  mutate('/payables');
};

// Offers mutations
export const createOffer = async (data: Record<string, unknown>, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  if (files?.length) {
    files.forEach((f) => formData.append('files', f));
  }
  const response = await api.post('/offers', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  mutate('/offers');
  return response.data;
};

export const updateOffer = async (id: number, data: Record<string, unknown>, files?: File[], filesToDetach?: number[]) => {
  const formData = new FormData();
  formData.append('json_data', JSON.stringify(data));
  if (files?.length) {
    files.forEach((f) => formData.append('files', f));
  }
  if (filesToDetach?.length) {
    formData.append('filesToDetach', JSON.stringify(filesToDetach));
  }
  const response = await api.patch(`/offers/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  mutate(`/offers/${id}`);
  mutate('/offers');
  return response.data;
};

export const deleteOffer = async (id: number) => {
  await api.delete(`/offers/${id}`);
  mutate(`/offers/${id}`);
  mutate('/offers');
};

export const duplicateOffer = async (id: number): Promise<{ id: number }> => {
  const response = await api.post(`/offers/${id}/duplicate`);
  mutate('/offers');
  return response.data;
};

// Catalog items mutations
export const createCatalogItem = async (data: { type: string; name: string; description?: string; defaultPrice?: number; manufacturerId?: number; categoryId?: number; isActive?: boolean; sortOrder?: number }) => {
  const response = await api.post('/catalog-items', data);
  mutate('/catalog-items');
  return response.data;
};

export const updateCatalogItem = async (id: number, data: Partial<{ type: string; name: string; description?: string; defaultPrice?: number; manufacturerId?: number | null; categoryId?: number | null; isActive?: boolean; sortOrder?: number; primaryFileAttachmentId?: number | null }>) => {
  const response = await api.patch(`/catalog-items/${id}`, data);
  mutate(`/catalog-items/${id}`);
  mutate('/catalog-items');
  return response.data;
};

export const deleteCatalogItem = async (id: number) => {
  await api.delete(`/catalog-items/${id}`);
  mutate(`/catalog-items/${id}`);
  mutate('/catalog-items');
};

export const duplicateCatalogItem = async (id: number) => {
  const response = await api.post(`/catalog-items/${id}/duplicate`);
  mutate('/catalog-items');
  mutate(`/catalog-items/${response.data.id}`);
  return response.data;
};

export const uploadCatalogItemFiles = async (id: number, files: File[]) => {
  const formData = new FormData();
  files.forEach((f) => formData.append('files', f));
  const response = await api.post(`/catalog-items/${id}/files`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  mutate(`/catalog-items/${id}`);
  mutate('/catalog-items');
  return response.data;
};

export const deleteCatalogItemFile = async (itemId: number, fileId: number) => {
  const response = await api.delete(`/catalog-items/${itemId}/files/${fileId}`);
  mutate(`/catalog-items/${itemId}`);
  mutate('/catalog-items');
  return response.data;
};

// Payees mutations (for Payable / RecurringPayable select)
export const createPayee = async (data: {
  name: string;
  notes?: string;
  email?: string;
  phone?: string;
  address?: string;
  vatNumber?: string;
  isActive?: boolean;
  defaultCategoryId?: number | null;
  defaultSubcategoryId?: number | null;
}) => {
  const response = await api.post('/payees', data);
  mutate((key) => typeof key === 'string' && key.startsWith('/payees'), undefined, { revalidate: true });
  return response.data;
};

export const updatePayee = async (
  id: number,
  data: {
    name?: string;
    notes?: string;
    email?: string;
    phone?: string;
    address?: string;
    vatNumber?: string;
    isActive?: boolean;
    defaultCategoryId?: number | null;
    defaultSubcategoryId?: number | null;
  }
) => {
  const response = await api.patch(`/payees/${id}`, data);
  mutate(`/payees/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/payees'), undefined, { revalidate: true });
  return response.data;
};

export const deletePayee = async (id: number) => {
  await api.delete(`/payees/${id}`);
  mutate(`/payees/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/payees'), undefined, { revalidate: true });
};

export const createOfferTemplate = async (data: { name: string; isDefault?: boolean; introText?: string; deliveryText?: string; paymentMethodText?: string; notes?: string }) => {
  const response = await api.post('/offer-templates', data);
  mutate('/offer-templates');
  return response.data;
};

export const updateOfferTemplate = async (id: number, data: Partial<{ name: string; isDefault?: boolean; introText?: string; deliveryText?: string; paymentMethodText?: string; notes?: string }>) => {
  const response = await api.patch(`/offer-templates/${id}`, data);
  mutate(`/offer-templates/${id}`);
  mutate('/offer-templates');
  return response.data;
};

export const deleteOfferTemplate = async (id: number) => {
  await api.delete(`/offer-templates/${id}`);
  mutate(`/offer-templates/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/offer-templates'), undefined, { revalidate: true });
};

export const markPayablePaid = async (id: number, paymentDate: string, paymentAmount?: number) => {
  const response = await api.patch(`/payables/${id}/mark-paid`, { paymentDate, paymentAmount });
  mutate(`/payables/${id}`);
  mutate('/payables');
  return response.data;
};

export const createRecurringPayable = async (data: any) => {
  const response = await api.post('/recurring-payables', data);
  mutate('/recurring-payables');
  return response.data;
};

export const updateRecurringPayable = async (id: number, data: any) => {
  const response = await api.patch(`/recurring-payables/${id}`, data);
  mutate(`/recurring-payables/${id}`);
  mutate('/recurring-payables');
  return response.data;
};

export const deleteRecurringPayable = async (id: number) => {
  await api.delete(`/recurring-payables/${id}`);
  mutate(`/recurring-payables/${id}`);
  mutate('/recurring-payables');
};

/** Permanently delete a file attachment record and the file from disk (for Deleted Files list). */
export const permanentDeleteFileAttachment = async (id: number) => {
  await api.delete(`/file-attachment/${id}/permanent`);
  mutate('/file-attachment/deleted');
};

/** Delete all FileAttachment records that have no file on disk (orphaned) or no filePath. */
export const deleteOrphanedFileAttachments = async (): Promise<{ deleted: number }> => {
  const res = await api.delete<{ deleted: number }>('/file-attachment/audit/orphaned');
  mutate('/file-attachment/deleted');
  return res.data;
};

/** Delete from disk all files that have no FileAttachment record. */
export const deleteFilesWithoutRecord = async (): Promise<{ deleted: number }> => {
  const res = await api.delete<{ deleted: number }>('/file-attachment/audit/files-without-record');
  return res.data;
};

// Parameter module mutations - Support Settings
export const createTaskType = async (data: any) => {
  const response = await api.post('/p-crm-task-type', data);
  mutate('/p-crm-task-type');
  return response.data;
};

export const updateTaskType = async (id: number, data: any) => {
  const response = await api.patch(`/p-crm-task-type/${id}`, data);
  mutate(`/p-crm-task-type/${id}`);
  mutate('/p-crm-task-type');
  return response.data;
};

export const deleteTaskType = async (id: number) => {
  await api.delete(`/p-crm-task-type/${id}`);
  mutate('/p-crm-task-type');
};

export const createTaskCategory = async (data: any) => {
  const response = await api.post('/p-crm-task-category', data);
  mutate('/p-crm-task-category');
  return response.data;
};

export const updateTaskCategory = async (id: number, data: any) => {
  const response = await api.patch(`/p-crm-task-category/${id}`, data);
  mutate(`/p-crm-task-category/${id}`);
  mutate('/p-crm-task-category');
  return response.data;
};

export const deleteTaskCategory = async (id: number) => {
  await api.delete(`/p-crm-task-category/${id}`);
  mutate('/p-crm-task-category');
};

export const createTaskStatus = async (data: any) => {
  const response = await api.post('/p-crm-task-status', data);
  mutate('/p-crm-task-status');
  return response.data;
};

export const updateTaskStatus = async (id: number, data: any) => {
  const response = await api.patch(`/p-crm-task-status/${id}`, data);
  mutate(`/p-crm-task-status/${id}`);
  mutate('/p-crm-task-status');
  return response.data;
};

export const deleteTaskStatus = async (id: number) => {
  await api.delete(`/p-crm-task-status/${id}`);
  mutate('/p-crm-task-status');
};

export const createPriority = async (data: any) => {
  const response = await api.post('/p-crm-priority', data);
  mutate('/p-crm-priority');
  return response.data;
};

export const updatePriority = async (id: number, data: any) => {
  const response = await api.patch(`/p-crm-priority/${id}`, data);
  mutate(`/p-crm-priority/${id}`);
  mutate('/p-crm-priority');
  return response.data;
};

export const deletePriority = async (id: number) => {
  await api.delete(`/p-crm-priority/${id}`);
  mutate('/p-crm-priority');
};

export const createTicketStatus = async (data: any) => {
  const response = await api.post('/p-crm-ticket-status', data);
  mutate('/p-crm-ticket-status');
  return response.data;
};

export const updateTicketStatus = async (id: number, data: any) => {
  const response = await api.patch(`/p-crm-ticket-status/${id}`, data);
  mutate(`/p-crm-ticket-status/${id}`);
  mutate('/p-crm-ticket-status');
  return response.data;
};

export const deleteTicketStatus = async (id: number) => {
  await api.delete(`/p-crm-ticket-status/${id}`);
  mutate('/p-crm-ticket-status');
};

export const createCallType = async (data: any) => {
  const response = await api.post('/call-type', data);
  mutate('/call-type');
  return response.data;
};

export const updateCallType = async (id: number, data: any) => {
  const response = await api.patch(`/call-type/${id}`, data);
  mutate(`/call-type/${id}`);
  mutate('/call-type');
  return response.data;
};

export const deleteCallType = async (id: number) => {
  await api.delete(`/call-type/${id}`);
  mutate('/call-type');
};

// Parameter module mutations - Service Settings
export const createServiceType = async (data: any) => {
  const response = await api.post('/p-crm-service-type', data);
  mutate('/p-crm-service-type');
  return response.data;
};

export const updateServiceType = async (id: number, data: any) => {
  const response = await api.patch(`/p-crm-service-type/${id}`, data);
  mutate(`/p-crm-service-type/${id}`);
  mutate('/p-crm-service-type');
  return response.data;
};

export const deleteServiceType = async (id: number) => {
  await api.delete(`/p-crm-service-type/${id}`);
  mutate('/p-crm-service-type');
};

export const createFrequencyType = async (data: any) => {
  const response = await api.post('/p-frequency-type', data);
  mutate('/p-frequency-type');
  return response.data;
};

export const updateFrequencyType = async (id: number, data: any) => {
  const response = await api.patch(`/p-frequency-type/${id}`, data);
  mutate(`/p-frequency-type/${id}`);
  mutate('/p-frequency-type');
  return response.data;
};

export const deleteFrequencyType = async (id: number) => {
  await api.delete(`/p-frequency-type/${id}`);
  mutate('/p-frequency-type');
};

export const createDuration = async (data: any) => {
  const response = await api.post('/p-crm-duration', data);
  mutate('/p-crm-duration');
  return response.data;
};

export const updateDuration = async (id: number, data: any) => {
  const response = await api.patch(`/p-crm-duration/${id}`, data);
  mutate(`/p-crm-duration/${id}`);
  mutate('/p-crm-duration');
  return response.data;
};

export const deleteDuration = async (id: number) => {
  await api.delete(`/p-crm-duration/${id}`);
  mutate('/p-crm-duration');
};

export const createServiceCategory = async (data: any) => {
  const response = await api.post('/p-crm-service-category', data);
  mutate('/p-crm-service-category');
  return response.data;
};

export const updateServiceCategory = async (id: number, data: any) => {
  const response = await api.patch(`/p-crm-service-category/${id}`, data);
  mutate(`/p-crm-service-category/${id}`);
  mutate('/p-crm-service-category');
  return response.data;
};

export const deleteServiceCategory = async (id: number) => {
  await api.delete(`/p-crm-service-category/${id}`);
  mutate('/p-crm-service-category');
};

// Parameter module mutations - Reminder Settings
export const createReminderType = async (data: any) => {
  const response = await api.post('/p-crm-reminder-type', data);
  mutate('/p-crm-reminder-type');
  return response.data;
};

export const updateReminderType = async (id: number, data: any) => {
  const response = await api.patch(`/p-crm-reminder-type/${id}`, data);
  mutate(`/p-crm-reminder-type/${id}`);
  mutate('/p-crm-reminder-type');
  return response.data;
};

export const deleteReminderType = async (id: number) => {
  await api.delete(`/p-crm-reminder-type/${id}`);
  mutate('/p-crm-reminder-type');
};

// Parameter module mutations - Business Settings
export const createTaxAuthority = async (data: any) => {
  const response = await api.post('/p-tax-authority', data);
  mutate('/p-tax-authority');
  return response.data;
};

export const updateTaxAuthority = async (id: number, data: any) => {
  const response = await api.patch(`/p-tax-authority/${id}`, data);
  mutate(`/p-tax-authority/${id}`);
  mutate('/p-tax-authority');
  return response.data;
};

export const deleteTaxAuthority = async (id: number) => {
  await api.delete(`/p-tax-authority/${id}`);
  mutate('/p-tax-authority');
};

export const createCountry = async (data: any) => {
  const response = await api.post('/p-country', data);
  mutate('/p-country');
  return response.data;
};

export const updateCountry = async (id: number, data: any) => {
  const response = await api.patch(`/p-country/${id}`, data);
  mutate(`/p-country/${id}`);
  mutate('/p-country');
  return response.data;
};

export const deleteCountry = async (id: number) => {
  await api.delete(`/p-country/${id}`);
  mutate('/p-country');
};

export const createAppStatus = async (data: any) => {
  const response = await api.post('/p-crm-app-status', data);
  mutate('/p-crm-app-status');
  return response.data;
};

export const updateAppStatus = async (id: number, data: any) => {
  const response = await api.patch(`/p-crm-app-status/${id}`, data);
  mutate(`/p-crm-app-status/${id}`);
  mutate('/p-crm-app-status');
  return response.data;
};

export const deleteAppStatus = async (id: number) => {
  await api.delete(`/p-crm-app-status/${id}`);
  mutate('/p-crm-app-status');
};

// Parameter module mutations - Finance Settings
export const createInvoiceCategory = async (data: any) => {
  const response = await api.post('/p-crm-invoice-category', data);
  mutate('/p-crm-invoice-category');
  return response.data;
};

export const updateInvoiceCategory = async (id: number, data: any) => {
  const response = await api.patch(`/p-crm-invoice-category/${id}`, data);
  mutate(`/p-crm-invoice-category/${id}`);
  mutate('/p-crm-invoice-category');
  return response.data;
};

export const deleteInvoiceCategory = async (id: number) => {
  await api.delete(`/p-crm-invoice-category/${id}`);
  mutate('/p-crm-invoice-category');
};

export const createInvoiceType = async (data: any) => {
  const response = await api.post('/p-crm-invoice-type', data);
  mutate('/p-crm-invoice-type');
  return response.data;
};

export const updateInvoiceType = async (id: number, data: any) => {
  const response = await api.patch(`/p-crm-invoice-type/${id}`, data);
  mutate(`/p-crm-invoice-type/${id}`);
  mutate('/p-crm-invoice-type');
  return response.data;
};

export const deleteInvoiceType = async (id: number) => {
  await api.delete(`/p-crm-invoice-type/${id}`);
  mutate('/p-crm-invoice-type');
};

// Parameter module mutations - Other
export const createTag = async (data: any) => {
  const response = await api.post('/tags', data);
  mutate('/tags');
  return response.data;
};

export const updateTag = async (id: number, data: any) => {
  const response = await api.patch(`/tags/${id}`, data);
  mutate(`/tags/${id}`);
  mutate('/tags');
  return response.data;
};

export const deleteTag = async (id: number) => {
  await api.delete(`/tags/${id}`);
  mutate('/tags');
};

// Expense Category mutations
export const createExpenseCategory = async (data: any) => {
  const response = await api.post('/crm-expense-category', data);
  mutate('/crm-expense-category');
  return response.data;
};

export const updateExpenseCategory = async (id: number, data: any) => {
  const response = await api.patch(`/crm-expense-category/${id}`, data);
  mutate(`/crm-expense-category/${id}`);
  mutate('/crm-expense-category');
  return response.data;
};

export const deleteExpenseCategory = async (id: number) => {
  await api.delete(`/crm-expense-category/${id}`);
  mutate('/crm-expense-category');
};

// Expense Sub Category mutations
export const createExpenseSubCategory = async (data: any) => {
  const response = await api.post('/crm-expense-sub-category', data);
  mutate('/crm-expense-sub-category');
  return response.data;
};

export const updateExpenseSubCategory = async (id: number, data: any) => {
  const response = await api.patch(`/crm-expense-sub-category/${id}`, data);
  mutate(`/crm-expense-sub-category/${id}`);
  mutate('/crm-expense-sub-category');
  return response.data;
};

export const deleteExpenseSubCategory = async (id: number) => {
  await api.delete(`/crm-expense-sub-category/${id}`);
  mutate('/crm-expense-sub-category');
};

// Project mutations
export const createProject = async (data: any) => {
  const response = await api.post('/crm-projects', data);
  mutate('/crm-projects');
  return response.data;
};

export const updateProject = async (id: number, data: any) => {
  const response = await api.patch(`/crm-projects/${id}`, data);
  mutate(`/crm-projects/${id}`);
  mutate('/crm-projects');
  return response.data;
};

export const deleteProject = async (id: number) => {
  await api.delete(`/crm-projects/${id}`);
  mutate('/crm-projects');
};

// Package mutations
export const createPackage = async (data: any) => {
  const response = await api.post('/crm-packages', data);
  mutate('/crm-packages');
  return response.data;
};

export const updatePackage = async (id: number, data: any) => {
  const response = await api.patch(`/crm-packages/${id}`, data);
  mutate(`/crm-packages/${id}`);
  mutate('/crm-packages');
  return response.data;
};

export const deletePackage = async (id: number) => {
  await api.delete(`/crm-packages/${id}`);
  mutate('/crm-packages');
};

// User mutations
function normalizeUserWritePayload(data: Record<string, unknown>): Record<string, unknown> {
  const out = { ...data };
  if (out.vesselId === '' || out.vesselId === undefined) {
    delete out.vesselId;
  } else if (out.vesselId != null) {
    const n = Number(out.vesselId);
    if (Number.isFinite(n) && n > 0) {
      out.vesselId = n;
    } else {
      delete out.vesselId;
    }
  }
  delete out.defaultLandingModuleKey;
  return out;
}

export const createUser = async (data: any) => {
  const payload = normalizeUserWritePayload({ ...data });
  const response = await api.post('/users', payload);
  mutate('/users');
  return response.data;
};

export const updateUser = async (id: number, data: any) => {
  // Remove password if it's empty (don't update password if not provided)
  const updateData = normalizeUserWritePayload({ ...data });
  if (updateData.userPassword === '' || updateData.userPassword === undefined) {
    delete updateData.userPassword;
  }
  
  const response = await api.patch(`/users/${id}`, updateData);
  mutate(`/users/${id}`);
  mutate('/users');
  return response.data;
};

export const deleteUser = async (id: number) => {
  await api.delete(`/users/${id}`);
  mutate('/users');
};

export const sendUserInvitation = async (data: { email: string; role?: string }) => {
  const response = await api.post('/users/invitations', data);
  mutate('/users');
  return response.data;
};

// Manufacturer mutations
export const createManufacturer = async (data: Partial<{ name: string }>) => {
  const response = await api.post('/manufacturers', data);
  mutate('/manufacturers');
  return response.data;
};

export const updateManufacturer = async (id: number, data: Partial<{ name: string }>) => {
  const response = await api.patch(`/manufacturers/${id}`, data);
  mutate(`/manufacturers/${id}`);
  mutate('/manufacturers');
  return response.data;
};

export const deleteManufacturer = async (id: number) => {
  await api.delete(`/manufacturers/${id}`);
  mutate('/manufacturers');
};

// Catalog Category mutations
export const createCatalogCategory = async (data: Partial<{ name: string }>) => {
  const response = await api.post('/catalog-categories', data);
  mutate('/catalog-categories');
  return response.data;
};

export const updateCatalogCategory = async (id: number, data: Partial<{ name: string }>) => {
  const response = await api.patch(`/catalog-categories/${id}`, data);
  mutate(`/catalog-categories/${id}`);
  mutate('/catalog-categories');
  return response.data;
};

export const deleteCatalogCategory = async (id: number) => {
  await api.delete(`/catalog-categories/${id}`);
  mutate('/catalog-categories');
};

export const createExampleItem = async (data: Record<string, unknown>) => {
  const response = await api.post('/example-items', data);
  mutate('/example-items');
  return response.data;
};

export const updateExampleItem = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/example-items/${id}`, data);
  mutate(`/example-items/${id}`);
  mutate('/example-items');
  return response.data;
};

export const deleteExampleItem = async (id: number) => {
  await api.delete(`/example-items/${id}`);
  mutate('/example-items');
};

export const createStoreUnit = async (data: Record<string, unknown>) => {
  const response = await api.post('/store-units', data);
  mutate('/store-units');
  return response.data;
};

export const updateStoreUnit = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/store-units/${id}`, data);
  mutate(`/store-units/${id}`);
  mutate('/store-units');
  return response.data;
};

export const deleteStoreUnit = async (id: number) => {
  await api.delete(`/store-units/${id}`);
  mutate('/store-units');
};

const coerceStoreItemPayload = (data: Record<string, unknown>) => {
  const out = { ...data };
  const nullableNumberFields = ['unitId', 'groupId', 'catalogId', 'stockMin', 'stockMax'];

  for (const field of nullableNumberFields) {
    const value = out[field];
    if (value === '' || value === null || value === undefined) {
      out[field] = null;
    } else {
      out[field] = Number(value);
    }
  }

  return out;
};

export const createStoreItem = async (data: Record<string, unknown>) => {
  const response = await api.post('/store-items', coerceStoreItemPayload(data));
  mutate('/store-items');
  return response.data;
};

export const updateStoreItem = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/store-items/${id}`, coerceStoreItemPayload(data));
  mutate(`/store-items/${id}`);
  mutate('/store-items');
  return response.data;
};

export const deleteStoreItem = async (id: number) => {
  await api.delete(`/store-items/${id}`);
  mutate('/store-items');
};

const coerceStoreCatalogPayload = (data: Record<string, unknown>) => {
  const out = { ...data };
  const n = out.sortOrder;
  if (n === '' || n === null || n === undefined) {
    out.sortOrder = 0;
  } else {
    out.sortOrder = Number(n);
  }
  return out;
};

export const createStoreCatalog = async (data: Record<string, unknown>) => {
  const response = await api.post('/store-catalogs', coerceStoreCatalogPayload(data));
  mutate('/store-catalogs');
  return response.data;
};

export const updateStoreCatalog = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/store-catalogs/${id}`, coerceStoreCatalogPayload(data));
  mutate(`/store-catalogs/${id}`);
  mutate('/store-catalogs');
  return response.data;
};

export const deleteStoreCatalog = async (id: number) => {
  await api.delete(`/store-catalogs/${id}`);
  mutate('/store-catalogs');
};

const coerceStoreItemGroupPayload = (data: Record<string, unknown>) => {
  const out = { ...data };
  for (const field of ['catalogId', 'parentId', 'sortOrder'] as const) {
    const value = out[field];
    if (value === '' || value === null || value === undefined) {
      out[field] = field === 'parentId' ? null : field === 'sortOrder' ? 0 : null;
    } else {
      out[field] = Number(value);
    }
  }
  return out;
};

export const createStoreItemGroup = async (data: Record<string, unknown>) => {
  const response = await api.post('/store-item-groups', coerceStoreItemGroupPayload(data));
  mutate('/store-item-groups');
  return response.data;
};

export const updateStoreItemGroup = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/store-item-groups/${id}`, coerceStoreItemGroupPayload(data));
  mutate(`/store-item-groups/${id}`);
  mutate('/store-item-groups');
  return response.data;
};

export const deleteStoreItemGroup = async (id: number) => {
  await api.delete(`/store-item-groups/${id}`);
  mutate('/store-item-groups');
};

const coerceVesselPayload = (data: Record<string, unknown>) => {
  const out = { ...data };
  const nullableNumberFields = [
    'vesselTypeId',
    'flagId',
    'ownerId',
    'managerId',
    'engId',
    'ecdisType',
    'grt',
    'nrt',
    'dwt',
    'safeManning',
  ];

  for (const field of nullableNumberFields) {
    const value = out[field];
    if (value === '' || value === null || value === undefined) {
      out[field] = null;
    } else {
      out[field] = Number(value);
    }
  }

  // Drop relation objects used only for select pinning / display.
  delete out.vesselType;
  delete out.flag;
  delete out.owner;
  delete out.manager;
  delete out.engineType;
  delete out.ecdisTypeRef;
  delete out.id;
  delete out.createdAt;
  delete out.updatedAt;

  return out;
};

export const createVessel = async (data: Record<string, unknown>) => {
  const response = await api.post('/vessels', coerceVesselPayload(data));
  mutate('/vessels');
  return response.data;
};

export const updateVessel = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/vessels/${id}`, coerceVesselPayload(data));
  mutate(`/vessels/${id}`);
  mutate('/vessels');
  return response.data;
};

export const deleteVessel = async (id: number) => {
  await api.delete(`/vessels/${id}`);
  mutate('/vessels');
};

const coerceSupplyCasePayload = (data: Record<string, unknown>) => {
  const out = { ...data };
  const nullableNumberFields = ['vesselId', 'statusId', 'portId'];

  for (const field of nullableNumberFields) {
    const value = out[field];
    if (value === '' || value === null || value === undefined) {
      out[field] = null;
    } else {
      out[field] = Number(value);
    }
  }

  const nullableDateFields = ['caseDate', 'eta'];
  for (const field of nullableDateFields) {
    const value = out[field];
    if (value === '' || value === null || value === undefined) {
      out[field] = null;
    }
  }

  if (Array.isArray(out.categoryIds)) {
    out.categoryIds = (out.categoryIds as unknown[])
      .map((x) => {
        if (x === '' || x === null || x === undefined) return null;
        const n = Number(x);
        return Number.isNaN(n) ? null : n;
      })
      .filter((x): x is number => x !== null);
  }

  return out;
};

/** Preview next reference from template without allocating sequence (aligns with next save unless counters change). */
export const peekReference = async (payload: Record<string, unknown>) => {
  const response = await api.post<{ ref: string | null }>('/reference-templates/peek-reference', payload);
  return response.data;
};

/** @deprecated Prefer peekReference — same unified API. */
export const peekSupplyReference = peekReference;

export const createSupplyCase = async (data: Record<string, unknown>) => {
  const response = await api.post('/supply-cases', coerceSupplyCasePayload(data));
  mutate('/supply-cases');
  return response.data;
};

export const updateSupplyCase = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/supply-cases/${id}`, coerceSupplyCasePayload(data));
  mutate(`/supply-cases/${id}`);
  mutate('/supply-cases');
  return response.data;
};

export const deleteSupplyCase = async (id: number) => {
  await api.delete(`/supply-cases/${id}`);
  mutate('/supply-cases');
};

const coerceSupplyRequisitionPayload = (data: Record<string, unknown>) => {
  const out = { ...data } as Record<string, unknown>;
  const nullableNumberFields = ['caseId', 'vesselId', 'refN'];
  for (const field of nullableNumberFields) {
    const value = out[field];
    if (value === '' || value === null || value === undefined) {
      out[field] = null;
    } else {
      out[field] = Number(value);
    }
  }

  if (Array.isArray(out.categoryIds)) {
    out.categoryIds = (out.categoryIds as unknown[])
      .map((x) => {
        if (x === '' || x === null || x === undefined) return null;
        const n = Number(x);
        return Number.isNaN(n) ? null : n;
      })
      .filter((x): x is number => x !== null);
  }

  if (Array.isArray(out.lines)) {
    out.lines = (out.lines as Record<string, unknown>[]).map((line) => {
      const next = { ...line };
      for (const field of ['itemId', 'unitId', 'qty', 'rob']) {
        const value = next[field];
        if (value === '' || value === null || value === undefined) {
          next[field] = null;
        } else {
          next[field] = Number(value);
        }
      }
      return next;
    });
  }

  return out;
};

export const createSupplyRequisition = async (data: Record<string, unknown>) => {
  const response = await api.post('/supply-requisitions', coerceSupplyRequisitionPayload(data));
  mutate((key) => typeof key === 'string' && key.startsWith('/supply-requisitions'), undefined, {
    revalidate: true,
  });
  return response.data;
};

export type SupplyRequisitionExcelImportResult = {
  ref: string | null;
  reqDate: string | null;
  vesselId: number | null;
  vesselNameRaw: string | null;
  categoryIds: number[];
  categoryNameRaw: string | null;
  notes: string | null;
  lineCount: number;
  warnings: string[];
  lines: Array<{
    itemVcode?: string | null;
    itemDescr?: string | null;
    unitId?: number | null;
    unitUcodeRaw?: string | null;
    unitMatched: boolean;
    qty?: number | null;
    rob?: number | null;
    itemNotes?: string | null;
    itemId?: number | null;
    level1Filter: string;
  }>;
};

/** Parses .xlsx using the active REQUISITION Excel document template mapping. */
export const importSupplyRequisitionFromExcel = async (
  file: File,
): Promise<SupplyRequisitionExcelImportResult> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post<SupplyRequisitionExcelImportResult>(
    '/supply-requisitions/import-excel',
    formData,
  );
  return response.data;
};

export const updateSupplyRequisition = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/supply-requisitions/${id}`, coerceSupplyRequisitionPayload(data));
  mutate(`/supply-requisitions/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/supply-requisitions'), undefined, {
    revalidate: true,
  });
  return response.data;
};

/** New draft requisition with same lines/header fields (case and refs cleared). */
export const duplicateSupplyRequisition = async (id: number) => {
  const response = await api.post(`/supply-requisitions/${id}/duplicate`);
  mutate((key) => typeof key === 'string' && key.startsWith('/supply-requisitions'), undefined, {
    revalidate: true,
  });
  return response.data;
};

export const createSupplyRequisitionTemplate = async (data: { name: string; lines: Record<string, unknown>[] }) => {
  const response = await api.post('/supply-requisition-templates', data);
  mutate('/supply-requisition-templates');
  return response.data;
};

/** Permanently deletes the requisition and its lines (blocked when RFQ / quotation / PO is linked). */
export const deleteSupplyRequisition = async (id: number) => {
  await api.delete(`/supply-requisitions/${id}`);
  mutate(`/supply-requisitions/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/supply-requisitions'), undefined, {
    revalidate: true,
  });
};

/** Clears `caseId` only; requisition remains (use from supply case custom view). */
export const unlinkSupplyRequisitionFromCase = async (id: number) => {
  await api.post(`/supply-requisitions/${id}/unlink-from-case`);
  mutate(`/supply-requisitions/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/supply-requisitions'), undefined, {
    revalidate: true,
  });
};

const coerceSupplierEnquiryPayload = (data: Record<string, unknown>) => {
  const out = { ...data } as Record<string, unknown>;
  for (const field of ['caseId', 'supplierId', 'requisitionId']) {
    const value = out[field];
    if (value === '' || value === null || value === undefined) {
      out[field] = null;
    } else {
      out[field] = Number(value);
    }
  }

  if (Array.isArray(out.lines)) {
    out.lines = (out.lines as Record<string, unknown>[]).map((line) => {
      const next = { ...line };
      for (const field of ['itemId', 'unitId', 'qty', 'requisitionQty']) {
        const v = next[field];
        if (v === '' || v === null || v === undefined) {
          next[field] = null;
        } else {
          next[field] = Number(v);
        }
      }
      return next;
    });
  }

  return out;
};

export const createSupplierEnquiry = async (data: Record<string, unknown>) => {
  const response = await api.post('/supplier-enquiries', coerceSupplierEnquiryPayload(data));
  mutate('/supplier-enquiries');
  return response.data;
};

export const updateSupplierEnquiry = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/supplier-enquiries/${id}`, coerceSupplierEnquiryPayload(data));
  mutate(`/supplier-enquiries/${id}`);
  mutate('/supplier-enquiries');
  return response.data;
};

export const deleteSupplierEnquiry = async (id: number) => {
  await api.delete(`/supplier-enquiries/${id}`);
  mutate('/supplier-enquiries');
};

export const createSupplierEnquiryFromRequisition = async (data: {
  requisitionId: number;
  supplierId: number;
  enqDate?: string;
  ref?: string;
  notes?: string;
}) => {
  const response = await api.post('/supplier-enquiries/from-requisition', data);
  mutate('/supplier-enquiries');
  return response.data;
};

export const createSupplierEnquiryFromRequisitionMulti = async (data: {
  requisitionId: number;
  supplierIds: number[];
  enqDate?: string;
  ref?: string;
  notes?: string;
}) => {
  const response = await api.post('/supplier-enquiries/from-requisition-multi', data);
  mutate('/supplier-enquiries');
  return response.data as Array<{ id: number }>;
};

export const createSupplierEnquiryRfqLink = async (
  enquiryId: number,
  body: { pin: string; expiresInDays: number },
) => {
  const response = await api.post(`/supplier-enquiries/${enquiryId}/rfq-link`, body);
  mutate(`/supplier-enquiries/${enquiryId}/rfq-link`);
  mutate(`/supplier-enquiries/${enquiryId}`);
  return response.data as { token: string; urlPath: string; fullUrl?: string };
};

export const revokeSupplierEnquiryRfqLink = async (enquiryId: number) => {
  await api.delete(`/supplier-enquiries/${enquiryId}/rfq-link`);
  mutate(`/supplier-enquiries/${enquiryId}/rfq-link`);
  mutate(`/supplier-enquiries/${enquiryId}`);
};

export const downloadSupplierEnquiryRfqTemplate = async (
  enquiryId: number,
): Promise<{ usedFallback: boolean }> => {
  const response = await api.get(`/supplier-enquiries/${enquiryId}/rfq-template`, {
    responseType: 'blob',
  });
  const fallbackRaw =
    response.headers['x-export-template-fallback'] ?? response.headers['X-Export-Template-Fallback'];
  const usedFallback = fallbackRaw === '1' || String(fallbackRaw).toLowerCase() === 'true';
  const cd = response.headers['content-disposition'];
  let filename = `RFQ-${enquiryId}-template.xlsx`;
  if (cd && typeof cd === 'string') {
    const m = /filename="?([^";]+)"?/i.exec(cd);
    if (m?.[1]) filename = m[1];
  }
  const url = window.URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
  return { usedFallback };
};

const revalidateExcelDocumentTemplates = () => {
  mutate((key) => typeof key === 'string' && key.startsWith('/excel-document-templates'));
};

export const uploadExcelDocumentTemplate = async (stage: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('stage', stage);
  const response = await api.post('/excel-document-templates', formData);
  revalidateExcelDocumentTemplates();
  return response.data;
};

export const updateExcelDocumentTemplateMapping = async (
  id: number,
  mapping: Record<string, unknown>,
) => {
  const response = await api.put(`/excel-document-templates/${id}/mapping`, { mapping });
  revalidateExcelDocumentTemplates();
  mutate(`/excel-document-templates/${id}/mapping`);
  return response.data;
};

export const publishExcelDocumentTemplate = async (id: number) => {
  const response = await api.patch(`/excel-document-templates/${id}/publish`);
  revalidateExcelDocumentTemplates();
  return response.data;
};

export const activateExcelDocumentTemplate = async (id: number) => {
  const response = await api.patch(`/excel-document-templates/${id}/activate`);
  revalidateExcelDocumentTemplates();
  return response.data;
};

export const deleteExcelDocumentTemplate = async (id: number) => {
  await api.delete(`/excel-document-templates/${id}`);
  revalidateExcelDocumentTemplates();
};

const revalidateReferenceTemplates = () => {
  mutate((key) => typeof key === 'string' && key.startsWith('/reference-templates'));
};

export const upsertReferenceTemplate = async (
  entityType: string,
  body: {
    pattern: string;
    sequenceScope: string;
    sequenceDigits: number;
    isActive?: boolean;
    allowManualReferenceEdit?: boolean;
    description?: string | null;
  },
) => {
  const response = await api.put(`/reference-templates/${encodeURIComponent(entityType)}`, body);
  revalidateReferenceTemplates();
  return response.data;
};

export const patchReferenceTemplateAllowManualEdit = async (
  entityType: string,
  allowManualReferenceEdit: boolean,
) => {
  const response = await api.patch(
    `/reference-templates/${encodeURIComponent(entityType)}/allow-manual-reference-edit`,
    { allowManualReferenceEdit },
  );
  revalidateReferenceTemplates();
  return response.data;
};

/** @deprecated Prefer upsertReferenceTemplate */
export const upsertSupplyReferenceTemplate = upsertReferenceTemplate;
/** @deprecated Prefer patchReferenceTemplateAllowManualEdit */
export const patchSupplyReferenceTemplateAllowManualEdit = patchReferenceTemplateAllowManualEdit;

export const downloadExcelDocumentTemplateOriginal = async (id: number) => {
  const response = await api.get(`/excel-document-templates/${id}/download`, {
    responseType: 'blob',
  });
  const cd = response.headers['content-disposition'];
  let filename = `template-${id}.xlsx`;
  if (cd && typeof cd === 'string') {
    const m = /filename="?([^";]+)"?/i.exec(cd);
    if (m?.[1]) filename = m[1];
  }
  const url = window.URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

export const downloadExcelDocumentTemplatePreviewFilled = async (id: number) => {
  const response = await api.get(`/excel-document-templates/${id}/preview-filled`, {
    responseType: 'blob',
  });
  const cd = response.headers['content-disposition'];
  let filename = `preview-${id}.xlsx`;
  if (cd && typeof cd === 'string') {
    const m = /filename="?([^";]+)"?/i.exec(cd);
    if (m?.[1]) filename = m[1];
  }
  const url = window.URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

export const importSupplierEnquiryRfqExcel = async (enquiryId: number, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post(`/supplier-enquiries/${enquiryId}/rfq-import`, formData);
  mutate(`/supplier-enquiries/${enquiryId}`);
  mutate('/supplier-quotations');
  return response.data;
};

const coerceSupplierQuotationPayload = (data: Record<string, unknown>) => {
  const out = { ...data } as Record<string, unknown>;
  for (const field of ['enqId', 'currencyId', 'rate']) {
    const value = out[field];
    if (value === '' || value === null || value === undefined) {
      out[field] = null;
    } else {
      out[field] = Number(value);
    }
  }

  if (Array.isArray(out.lineItems)) {
    out.lineItems = (out.lineItems as Record<string, unknown>[]).map((line) => {
      const next = { ...line };
      for (const field of [
        'itemId',
        'unitId',
        'qty',
        'unitPrice',
        'lineTotal',
        'discount',
        'vat',
        'requisitionQty',
        'enquiryQty',
      ]) {
        const v = next[field];
        if (v === '' || v === null || v === undefined) {
          next[field] = null;
        } else {
          next[field] = Number(v);
        }
      }
      return next;
    });
  }

  if (Array.isArray(out.expenses)) {
    out.expenses = (out.expenses as Record<string, unknown>[]).map((row) => {
      const next = { ...row };
      for (const field of ['value', 'vat']) {
        const v = next[field];
        if (v === '' || v === null || v === undefined) {
          next[field] = null;
        } else {
          next[field] = Number(v);
        }
      }
      return next;
    });
  }

  return out;
};

export const createSupplierQuotation = async (data: Record<string, unknown>) => {
  const response = await api.post('/supplier-quotations', coerceSupplierQuotationPayload(data));
  mutate('/supplier-quotations');
  return response.data;
};

export const updateSupplierQuotation = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/supplier-quotations/${id}`, coerceSupplierQuotationPayload(data));
  mutate(`/supplier-quotations/${id}`);
  mutate('/supplier-quotations');
  return response.data;
};

export const deleteSupplierQuotation = async (id: number) => {
  await api.delete(`/supplier-quotations/${id}`);
  mutate('/supplier-quotations');
};

export const createSupplierQuotationFromEnquiry = async (data: {
  enquiryId: number;
  quoDate?: string;
  ref?: string;
  notes?: string;
}) => {
  const response = await api.post('/supplier-quotations/from-enquiry', data);
  mutate('/supplier-quotations');
  return response.data;
};

const coerceSupplyInvoicePayload = (data: Record<string, unknown>): Record<string, unknown> => {
  const out = { ...data } as Record<string, unknown>;
  for (const field of ['purchaseOrderId', 'currencyId', 'refN', 'deliveryId', 'vesselId']) {
    const v = out[field];
    if (v === '' || v === null || v === undefined) {
      out[field] = v === undefined ? v : null;
    } else {
      out[field] = Number(v);
    }
  }
  if (out.exchangeRate === '' || out.exchangeRate === null || out.exchangeRate === undefined) {
    out.exchangeRate = null;
  } else {
    out.exchangeRate = Number(out.exchangeRate);
  }
  if (Array.isArray(out.lineItems)) {
    out.lineItems = (out.lineItems as Record<string, unknown>[]).map((line) => {
      const next = { ...line };
      for (const field of [
        'itemId',
        'unitId',
        'invoiceQty',
        'unitPrice',
        'lineTotal',
        'discount',
        'vat',
        'requisitionQty',
        'enquiryQty',
        'quotationQty',
        'deliveryQty',
        'purchaseOrderQty',
      ]) {
        const n = next[field];
        if (n === '' || n === null || n === undefined) {
          next[field] = null;
        } else {
          next[field] = Number(n);
        }
      }
      return next;
    });
  }
  if (Array.isArray(out.expenses)) {
    out.expenses = (out.expenses as Record<string, unknown>[]).map((row) => {
      const next = { ...row };
      for (const field of ['value', 'vat']) {
        const n = next[field];
        if (n === '' || n === null || n === undefined) {
          next[field] = null;
        } else {
          next[field] = Number(n);
        }
      }
      return next;
    });
  }
  return out;
};

export const createSupplyInvoice = async (data: Record<string, unknown>) => {
  const response = await api.post('/supply-invoices', coerceSupplyInvoicePayload(data));
  mutate('/supply-invoices');
  return response.data;
};

export const updateSupplyInvoice = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/supply-invoices/${id}`, coerceSupplyInvoicePayload(data));
  mutate(`/supply-invoices/${id}`);
  mutate('/supply-invoices');
  return response.data;
};

export const deleteSupplyInvoice = async (id: number) => {
  await api.delete(`/supply-invoices/${id}`);
  mutate('/supply-invoices');
};

export const createSupplyInvoiceFromPurchaseOrder = async (data: {
  purchaseOrderId: number;
  invDate?: string;
  ref?: string;
  notes?: string;
  deliveryId?: number;
  vesselId?: number;
}) => {
  const response = await api.post('/supply-invoices/from-purchase-order', data);
  mutate('/supply-invoices');
  return response.data;
};

const coerceSupplyPurchaseOrderPayload = (data: Record<string, unknown>) => {
  const out = { ...data } as Record<string, unknown>;
  for (const field of ['quotationId', 'currencyId', 'exchangeRate']) {
    const value = out[field];
    if (value === '' || value === null || value === undefined) {
      out[field] = null;
    } else {
      out[field] = Number(value);
    }
  }

  if (Array.isArray(out.lineItems)) {
    out.lineItems = (out.lineItems as Record<string, unknown>[]).map((line) => {
      const next = { ...line };
      for (const field of [
        'itemId',
        'unitId',
        'qty',
        'unitPrice',
        'lineTotal',
        'discount',
        'vat',
        'requisitionQty',
        'enquiryQty',
        'quotationQty',
      ]) {
        const v = next[field];
        if (v === '' || v === null || v === undefined) {
          next[field] = null;
        } else {
          next[field] = Number(v);
        }
      }
      return next;
    });
  }

  if (Array.isArray(out.expenses)) {
    out.expenses = (out.expenses as Record<string, unknown>[]).map((row) => {
      const next = { ...row };
      for (const field of ['value', 'vat']) {
        const v = next[field];
        if (v === '' || v === null || v === undefined) {
          next[field] = null;
        } else {
          next[field] = Number(v);
        }
      }
      return next;
    });
  }

  return out;
};

export const createSupplyPurchaseOrder = async (data: Record<string, unknown>) => {
  const response = await api.post('/supply-purchase-orders', coerceSupplyPurchaseOrderPayload(data));
  mutate('/supply-purchase-orders');
  return response.data;
};

export const updateSupplyPurchaseOrder = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/supply-purchase-orders/${id}`, coerceSupplyPurchaseOrderPayload(data));
  mutate(`/supply-purchase-orders/${id}`);
  mutate('/supply-purchase-orders');
  return response.data;
};

export const deleteSupplyPurchaseOrder = async (id: number) => {
  await api.delete(`/supply-purchase-orders/${id}`);
  mutate('/supply-purchase-orders');
};

export const createSupplyPurchaseOrderFromQuotation = async (data: {
  quotationId: number;
  orderDate?: string;
  ref?: string;
  notes?: string;
}) => {
  const response = await api.post('/supply-purchase-orders/from-quotation', data);
  mutate('/supply-purchase-orders');
  return response.data;
};

const coerceSupplyDeliveryPayload = (data: Record<string, unknown>) => {
  const out = { ...data } as Record<string, unknown>;
  for (const field of ['vesselId', 'caseId', 'supplierId', 'portCode', 'refN', 'supplyForwardingId']) {
    const value = out[field];
    if (value === '' || value === null || value === undefined) {
      if (field === 'portCode' || field === 'refN' || field === 'supplyForwardingId') {
        delete out[field];
      }
    } else {
      out[field] = Number(value);
    }
  }
  const poid = out.purchaseOrderId;
  if (poid === '' || poid === null || poid === undefined) {
    out.purchaseOrderId = null;
  } else {
    out.purchaseOrderId = Number(poid);
  }
  if (Array.isArray(out.lines)) {
    out.lines = (out.lines as Record<string, unknown>[]).map((line) => {
      const next = { ...line };
      for (const field of ['caseId', 'itemId', 'unitId', 'purchaseOrderQty', 'deliveryQty']) {
        const v = next[field];
        if (v === '' || v === null || v === undefined) {
          next[field] = null;
        } else {
          next[field] = Number(v);
        }
      }
      if (next.isDelivered === '' || next.isDelivered === null || next.isDelivered === undefined) {
        next.isDelivered = false;
      } else {
        next.isDelivered = Boolean(next.isDelivered);
      }
      return next;
    });
  }
  return out;
};

export const createSupplyDelivery = async (data: Record<string, unknown>) => {
  const response = await api.post('/supply-deliveries', coerceSupplyDeliveryPayload(data));
  mutate('/supply-deliveries');
  return response.data;
};

export const createSupplyDeliveryFromPurchaseOrder = async (data: Record<string, unknown>) => {
  const response = await api.post('/supply-deliveries/from-purchase-order', coerceSupplyDeliveryPayload(data));
  mutate('/supply-deliveries');
  mutate((key) => typeof key === 'string' && key.includes('/supply-purchase-orders/pending-for-vessel'));
  const fwd = data?.supplyForwardingId;
  if (fwd != null && fwd !== '') {
    const n = Number(fwd);
    if (Number.isFinite(n)) mutate(`/supply-forwardings/${n}`);
  }
  return response.data;
};

export const updateSupplyDelivery = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/supply-deliveries/${id}`, coerceSupplyDeliveryPayload(data));
  mutate(`/supply-deliveries/${id}`);
  mutate('/supply-deliveries');
  return response.data;
};

export const deleteSupplyDelivery = async (id: number) => {
  await api.delete(`/supply-deliveries/${id}`);
  mutate('/supply-deliveries');
};

const coerceSupplyForwardingStatusPayload = (data: Record<string, unknown>) => {
  const out = { ...data };
  if (out.sortOrder === '' || out.sortOrder === null || out.sortOrder === undefined) {
    out.sortOrder = 0;
  } else {
    out.sortOrder = Number(out.sortOrder);
  }
  if (out.isActive === '' || out.isActive === null) out.isActive = true;
  return out;
};

export const createSupplyForwardingStatus = async (data: Record<string, unknown>) => {
  const response = await api.post('/supply-forwarding-statuses', coerceSupplyForwardingStatusPayload(data));
  mutate('/supply-forwarding-statuses');
  return response.data;
};

export const updateSupplyForwardingStatus = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/supply-forwarding-statuses/${id}`, coerceSupplyForwardingStatusPayload(data));
  mutate(`/supply-forwarding-statuses/${id}`);
  mutate('/supply-forwarding-statuses');
  return response.data;
};

export const deleteSupplyForwardingStatus = async (id: number) => {
  await api.delete(`/supply-forwarding-statuses/${id}`);
  mutate('/supply-forwarding-statuses');
};

const coerceSupplyForwardingPayload = (data: Record<string, unknown>) => {
  const out = { ...data };
  for (const key of [
    'caseId',
    'purchaseOrderId',
    'forwarderId',
    'portId',
    'statusId',
    'priorityId',
    'warehouseLocationId',
    'packageCount',
  ]) {
    const v = out[key];
    if (v === '' || v === undefined) {
      out[key] = null;
    } else if (v != null) {
      out[key] = Number(v);
    }
  }
  for (const key of [
    'costUsd',
    'totalWeight',
    'warehouseCostUsd',
    'subAgentCostUsd',
    'vesselAgentCostUsd',
  ]) {
    const v = out[key];
    if (v === '' || v === undefined) {
      out[key] = null;
    } else if (v != null) {
      out[key] = Number(v);
    }
  }
  return out;
};

export const createSupplyForwarding = async (data: Record<string, unknown>) => {
  const response = await api.post('/supply-forwardings', coerceSupplyForwardingPayload(data));
  mutate('/supply-forwardings');
  return response.data;
};

export const updateSupplyForwarding = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/supply-forwardings/${id}`, coerceSupplyForwardingPayload(data));
  mutate(`/supply-forwardings/${id}`);
  mutate('/supply-forwardings');
  return response.data;
};

export const deleteSupplyForwarding = async (id: number) => {
  await api.delete(`/supply-forwardings/${id}`);
  mutate('/supply-forwardings');
};

export const createQuotationEvaluation = async (data: {
  caseId: number;
  quotationIds: number[];
  evaluationDate?: string;
  ref?: string;
  notes?: string;
}) => {
  const response = await api.post('/quotation-evaluations', data);
  mutate((key) => typeof key === 'string' && key.startsWith('/quotation-evaluations'));
  mutate((key) => typeof key === 'string' && key.includes('/case-view'));
  return response.data as { id: number };
};

export const updateQuotationEvaluation = async (
  id: number,
  data: Partial<{
    quotationIds: number[];
    evaluationDate: string | null;
    ref: string | null;
    notes: string | null;
  }>,
) => {
  const response = await api.patch(`/quotation-evaluations/${id}`, data);
  mutate(`/quotation-evaluations/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/quotation-evaluations'));
  mutate((key) => typeof key === 'string' && key.includes('/case-view'));
  return response.data;
};

export const deleteQuotationEvaluation = async (id: number) => {
  await api.delete(`/quotation-evaluations/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/quotation-evaluations'));
  mutate((key) => typeof key === 'string' && key.includes('/case-view'));
};

export const createCountryMaster = async (data: Partial<{ name: string; isoCode2?: string | null; isoCode3?: string | null; isActive?: boolean }>) => {
  const response = await api.post('/countries', data);
  mutate('/countries');
  return response.data;
};

export const updateCountryMaster = async (id: number, data: Partial<{ name: string; isoCode2?: string | null; isoCode3?: string | null; isActive?: boolean }>) => {
  const response = await api.patch(`/countries/${id}`, data);
  mutate(`/countries/${id}`);
  mutate('/countries');
  return response.data;
};

export const deleteCountryMaster = async (id: number) => {
  await api.delete(`/countries/${id}`);
  mutate('/countries');
};

const coerceCurrencyPayload = (data: Record<string, unknown>) => {
  const out = { ...data } as Record<string, unknown>;
  if (out.sortOrder === '' || out.sortOrder === null || out.sortOrder === undefined) {
    out.sortOrder = 0;
  } else {
    out.sortOrder = Number(out.sortOrder);
  }
  if (typeof out.code === 'string') {
    out.code = out.code.trim().toUpperCase();
  }
  return out;
};

export const createCurrency = async (data: Record<string, unknown>) => {
  const response = await api.post('/currencies', coerceCurrencyPayload(data));
  mutate('/currencies');
  return response.data;
};

export const updateCurrency = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/currencies/${id}`, coerceCurrencyPayload(data));
  mutate(`/currencies/${id}`);
  mutate('/currencies');
  return response.data;
};

export const deleteCurrency = async (id: number) => {
  await api.delete(`/currencies/${id}`);
  mutate('/currencies');
};

const coerceDepartmentPayload = (data: Record<string, unknown>) => {
  const out = { ...data } as Record<string, unknown>;
  if (out.sortOrder === '' || out.sortOrder === null || out.sortOrder === undefined) {
    out.sortOrder = 0;
  } else {
    out.sortOrder = Number(out.sortOrder);
  }
  if (typeof out.code === 'string') {
    out.code = out.code.trim().toUpperCase();
  }
  return out;
};

export const createDepartment = async (data: Record<string, unknown>) => {
  const response = await api.post('/departments', coerceDepartmentPayload(data));
  mutate('/departments');
  return response.data;
};

export const updateDepartment = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/departments/${id}`, coerceDepartmentPayload(data));
  mutate(`/departments/${id}`);
  mutate('/departments');
  return response.data;
};

export const deleteDepartment = async (id: number) => {
  await api.delete(`/departments/${id}`);
  mutate('/departments');
};

const coerceManagementCompanyPayload = (data: Record<string, unknown>) => {
  const out = { ...data } as Record<string, unknown>;
  if (out.sortOrder === '' || out.sortOrder === null || out.sortOrder === undefined) {
    out.sortOrder = 0;
  } else {
    out.sortOrder = Number(out.sortOrder);
  }
  if (typeof out.code === 'string') {
    out.code = out.code.trim().toUpperCase();
  }
  if (out.notes === '') {
    out.notes = null;
  }
  return out;
};

export const createManagementCompany = async (data: Record<string, unknown>) => {
  const response = await api.post('/management-companies', coerceManagementCompanyPayload(data));
  mutate('/management-companies');
  return response.data;
};

export const updateManagementCompany = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(
    `/management-companies/${id}`,
    coerceManagementCompanyPayload(data),
  );
  mutate(`/management-companies/${id}`);
  mutate('/management-companies');
  return response.data;
};

export const deleteManagementCompany = async (id: number) => {
  await api.delete(`/management-companies/${id}`);
  mutate('/management-companies');
  mutate('/vessels');
};

const coerceStoreCategoryPayload = (data: Record<string, unknown>) => {
  const out = { ...data };
  if (out.aprvAmnt === '' || out.aprvAmnt === null || out.aprvAmnt === undefined) {
    out.aprvAmnt = null;
  } else {
    out.aprvAmnt = Number(out.aprvAmnt);
  }
  if (out.aprvUser === '' || out.aprvUser === null || out.aprvUser === undefined) {
    out.aprvUser = null;
  } else {
    out.aprvUser = Number(out.aprvUser);
  }
  return out;
};

export const createStoreCategory = async (data: Record<string, unknown>) => {
  const response = await api.post('/store-categories', coerceStoreCategoryPayload(data));
  mutate('/store-categories');
  return response.data;
};

export const updateStoreCategory = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/store-categories/${id}`, coerceStoreCategoryPayload(data));
  mutate(`/store-categories/${id}`);
  mutate('/store-categories');
  return response.data;
};

export const deleteStoreCategory = async (id: number) => {
  await api.delete(`/store-categories/${id}`);
  mutate('/store-categories');
};

const coercePortPayload = (data: Record<string, unknown>) => {
  const out = { ...data };
  if (out.countryId === '' || out.countryId === null || out.countryId === undefined) {
    out.countryId = null;
  } else {
    out.countryId = Number(out.countryId);
  }
  return out;
};

export const createPort = async (data: Record<string, unknown>) => {
  const response = await api.post('/ports', coercePortPayload(data));
  mutate('/ports');
  return response.data;
};

export const updatePort = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/ports/${id}`, coercePortPayload(data));
  mutate(`/ports/${id}`);
  mutate('/ports');
  return response.data;
};

export const deletePort = async (id: number) => {
  await api.delete(`/ports/${id}`);
  mutate('/ports');
};

// --- Accounting ---

export const createAccDocumentType = async (data: Record<string, unknown>) => {
  const response = await api.post('/acc-document-types', data);
  mutate('/acc-document-types');
  return response.data;
};

export const updateAccDocumentType = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/acc-document-types/${id}`, data);
  mutate(`/acc-document-types/${id}`);
  mutate('/acc-document-types');
  return response.data;
};

export const deleteAccDocumentType = async (id: number) => {
  await api.delete(`/acc-document-types/${id}`);
  mutate('/acc-document-types');
};

export const createAccAccountType = async (data: Record<string, unknown>) => {
  const response = await api.post('/acc-account-types', data);
  mutate('/acc-account-types');
  return response.data;
};

export const updateAccAccountType = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/acc-account-types/${id}`, data);
  mutate(`/acc-account-types/${id}`);
  mutate('/acc-account-types');
  return response.data;
};

export const deleteAccAccountType = async (id: number) => {
  await api.delete(`/acc-account-types/${id}`);
  mutate('/acc-account-types');
};

export const createAccOperationalType = async (data: Record<string, unknown>) => {
  const response = await api.post('/acc-operational-types', data);
  mutate('/acc-operational-types');
  return response.data;
};

export const updateAccOperationalType = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/acc-operational-types/${id}`, data);
  mutate(`/acc-operational-types/${id}`);
  mutate('/acc-operational-types');
  return response.data;
};

export const deleteAccOperationalType = async (id: number) => {
  await api.delete(`/acc-operational-types/${id}`);
  mutate('/acc-operational-types');
};

export const createAccCompany = async (data: Record<string, unknown>) => {
  const response = await api.post('/acc-companies', data);
  mutate('/acc-companies');
  return response.data;
};

export const updateAccCompany = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/acc-companies/${id}`, data);
  mutate(`/acc-companies/${id}`);
  mutate('/acc-companies');
  return response.data;
};

export const deleteAccCompany = async (id: number) => {
  await api.delete(`/acc-companies/${id}`);
  mutate('/acc-companies');
};

export const createAccAccount = async (data: Record<string, unknown>) => {
  const response = await api.post('/acc-accounts', data);
  mutate('/acc-accounts');
  return response.data;
};

export const updateAccAccount = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/acc-accounts/${id}`, data);
  mutate(`/acc-accounts/${id}`);
  mutate('/acc-accounts');
  return response.data;
};

export const bulkUpdateAccAccounts = async (data: {
  ids: number[];
  accountTypeId?: number | null;
  operationalTypeId?: number | null;
  departmentId?: number | null;
  allowOpenItems?: boolean;
  isIntercompany?: boolean;
  isBudget?: boolean;
  isActive?: boolean;
}) => {
  const response = await api.patch('/acc-accounts/bulk', data);
  mutate('/acc-accounts');
  return response.data as { updated: number; ids: number[] };
};

export const deleteAccAccount = async (id: number) => {
  await api.delete(`/acc-accounts/${id}`);
  mutate('/acc-accounts');
};

export const createAccPeriod = async (data: Record<string, unknown>) => {
  const response = await api.post('/acc-periods', data);
  mutate('/acc-periods');
  return response.data;
};

export const updateAccPeriod = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/acc-periods/${id}`, data);
  mutate(`/acc-periods/${id}`);
  mutate('/acc-periods');
  return response.data;
};

export const deleteAccPeriod = async (id: number) => {
  await api.delete(`/acc-periods/${id}`);
  mutate('/acc-periods');
};

export const closeAccPeriod = async (id: number) => {
  const response = await api.post(`/acc-periods/${id}/close`);
  mutate(`/acc-periods/${id}`);
  mutate('/acc-periods');
  return response.data;
};

export const openAccPeriod = async (id: number) => {
  const response = await api.post(`/acc-periods/${id}/open`);
  mutate(`/acc-periods/${id}`);
  mutate('/acc-periods');
  return response.data;
};

export const createAccCurrencyRate = async (data: Record<string, unknown>) => {
  const response = await api.post('/acc-currency-rates', data);
  mutate('/acc-currency-rates');
  return response.data;
};

export const updateAccCurrencyRate = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/acc-currency-rates/${id}`, data);
  mutate(`/acc-currency-rates/${id}`);
  mutate('/acc-currency-rates');
  return response.data;
};

export const deleteAccCurrencyRate = async (id: number) => {
  await api.delete(`/acc-currency-rates/${id}`);
  mutate('/acc-currency-rates');
};

export type AccCurrencyRateOnlineFetchResult = {
  currencyCode: string;
  requestedDate: string;
  sourceDate: string;
  rate: number;
  source: 'frankfurter';
};

export type AccCurrencyRateOnlineSyncItemResult = {
  currencyId: number;
  currencyCode: string;
  status: 'created' | 'updated' | 'skipped' | 'failed';
  rate?: number;
  sourceDate?: string;
  message?: string;
};

export type AccCurrencyRateOnlineSyncResult = {
  rateDate: string;
  source: 'frankfurter';
  results: AccCurrencyRateOnlineSyncItemResult[];
  created: number;
  updated: number;
  skipped: number;
  failed: number;
};

export const fetchAccCurrencyRateOnline = async (currencyCode: string, rateDate: string) => {
  const params = new URLSearchParams({ currencyCode, rateDate });
  const response = await api.get(`/acc-currency-rates/fetch-online?${params.toString()}`);
  return response.data as AccCurrencyRateOnlineFetchResult;
};

export type AccCurrencyRateResolveResult = {
  currencyId: number;
  currencyCode: string;
  rate: number | null;
  rateDate: string | null;
};

export const resolveAccCurrencyRate = async (currencyId: number, asOfDate?: string) => {
  const params = new URLSearchParams({
    currencyId: String(currencyId),
  });
  if (asOfDate) params.set('asOfDate', asOfDate);
  const response = await api.get(`/acc-currency-rates/resolve?${params.toString()}`);
  return response.data as AccCurrencyRateResolveResult;
};

export const syncAccCurrencyRatesOnline = async (data: {
  rateDate: string;
  currencyIds?: number[];
  overwrite?: boolean;
}) => {
  const response = await api.post('/acc-currency-rates/sync-online', data);
  mutate('/acc-currency-rates');
  return response.data as AccCurrencyRateOnlineSyncResult;
};

export const syncAccStatementLinesFromCoa = async () => {
  const response = await api.post('/acc-statement-lines/sync-from-coa');
  mutate('/acc-statement-lines');
  return response.data as { created: number; total: number };
};

export const createAccStatementLine = async (data: Record<string, unknown>) => {
  const response = await api.post('/acc-statement-lines', data);
  mutate('/acc-statement-lines');
  return response.data;
};

export const updateAccStatementLine = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/acc-statement-lines/${id}`, data);
  mutate('/acc-statement-lines');
  return response.data;
};

export const deleteAccStatementLine = async (id: number) => {
  await api.delete(`/acc-statement-lines/${id}`);
  mutate('/acc-statement-lines');
};

export const allocateOpenItems = async (data: Record<string, unknown>) => {
  const response = await api.post('/accounting/open-items/allocate', data);
  revalidatePrefix('/accounting/open-items');
  revalidatePrefix('/acc-documents');
  return response.data as {
    documentId: number;
    documentTypeCode: string;
    totalAmount: number;
    allocationCount: number;
    saved: boolean;
  };
};

export const ensureOpenItemLinks = async (data: {
  companyId: number;
  accountId: number;
  lineIds: number[];
}) => {
  const response = await api.post('/accounting/open-items/ensure-links', data);
  revalidatePrefix('/accounting/open-items');
  revalidatePrefix('/acc-documents');
  return response.data as {
    assignments: { lineId: number; linkNumber: string; generated: boolean }[];
  };
};

export const createAccDocument = async (data: Record<string, unknown>) => {
  const response = await api.post('/acc-documents', data);
  revalidatePrefix('/acc-documents');
  return response.data;
};

export const updateAccDocument = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/acc-documents/${id}`, data);
  mutate(`/acc-documents/${id}`);
  revalidatePrefix('/acc-documents');
  return response.data;
};

export const deleteAccDocument = async (id: number) => {
  await api.delete(`/acc-documents/${id}`);
  revalidatePrefix('/acc-documents');
};

export const saveAccDocument = async (id: number) => {
  const response = await api.post(`/acc-documents/${id}/save`);
  mutate(`/acc-documents/${id}`);
  revalidatePrefix('/acc-documents');
  return response.data;
};

export const revertAccDocumentToDraft = async (id: number) => {
  const response = await api.post(`/acc-documents/${id}/revert-to-draft`);
  mutate(`/acc-documents/${id}`);
  revalidatePrefix('/acc-documents');
  return response.data;
};

export const uploadAccDocumentFiles = async (id: number, files: File[]) => {
  const formData = new FormData();
  files.forEach((f) => formData.append('files', f));
  const response = await api.post(`/acc-documents/${id}/files`, formData);
  mutate(`/acc-documents/${id}`);
  return response.data;
};

export const deleteAccDocumentFile = async (documentId: number, fileId: number) => {
  await api.delete(`/acc-documents/${documentId}/files/${fileId}`);
  mutate(`/acc-documents/${documentId}`);
};

export const createCrewLookup = async (kind: string, data: Record<string, unknown>) => {
  const response = await api.post(`/crew-lookups/${kind}`, coerceCrewLookupPayload(data));
  mutate((key) => typeof key === 'string' && key.startsWith(`/crew-lookups/${kind}`));
  mutate('/crew/lookups');
  return response.data;
};

export const updateCrewLookup = async (kind: string, id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/crew-lookups/${kind}/${id}`, coerceCrewLookupPayload(data));
  mutate(`/crew-lookups/${kind}/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith(`/crew-lookups/${kind}`));
  mutate('/crew/lookups');
  return response.data;
};

export const deleteCrewLookup = async (kind: string, id: number) => {
  await api.delete(`/crew-lookups/${kind}/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith(`/crew-lookups/${kind}`));
  mutate('/crew/lookups');
};

const coerceCrewWageColumnPayload = (data: Record<string, unknown>) => {
  const out = { ...data } as Record<string, unknown>;
  if (typeof out.code === 'string') {
    out.code = out.code.trim().toUpperCase();
  }
  if (typeof out.name === 'string') {
    out.name = out.name.trim();
  }
  if (out.sortOrder === '' || out.sortOrder === null || out.sortOrder === undefined) {
    out.sortOrder = 0;
  } else {
    out.sortOrder = Number(out.sortOrder);
  }
  if (out.notes === '') out.notes = null;
  return out;
};

export const createCrewWageColumn = async (data: Record<string, unknown>) => {
  const response = await api.post('/crew-wage/columns', coerceCrewWageColumnPayload(data));
  mutate((key) => typeof key === 'string' && key.startsWith('/crew-wage/columns'));
  mutate('/crew/lookups');
  return response.data;
};

export const updateCrewWageColumn = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/crew-wage/columns/${id}`, coerceCrewWageColumnPayload(data));
  mutate(`/crew-wage/columns/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/crew-wage/columns'));
  mutate('/crew/lookups');
  return response.data;
};

export const deleteCrewWageColumn = async (id: number) => {
  await api.delete(`/crew-wage/columns/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/crew-wage/columns'));
  mutate('/crew/lookups');
};

export const createMgaSheetColumn = async (data: Record<string, unknown>) => {
  const response = await api.post('/mga/sheet-columns', data);
  mutate((key) => typeof key === 'string' && key.startsWith('/mga/sheet-columns'));
  return response.data;
};

export const updateMgaSheetColumn = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/mga/sheet-columns/${id}`, data);
  mutate(`/mga/sheet-columns/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/mga/sheet-columns'));
  return response.data;
};

export const deleteMgaSheetColumn = async (id: number) => {
  await api.delete(`/mga/sheet-columns/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/mga/sheet-columns'));
};

export const createCrewWageScale = async (data: Record<string, unknown>) => {
  const response = await api.post('/crew-wage/scales', data);
  mutate((key) => typeof key === 'string' && key.startsWith('/crew-wage/scales'));
  return response.data;
};

export const updateCrewWageScale = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/crew-wage/scales/${id}`, data);
  mutate(`/crew-wage/scales/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/crew-wage/scales'));
  return response.data;
};

export const deleteCrewWageScale = async (id: number) => {
  await api.delete(`/crew-wage/scales/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/crew-wage/scales'));
};

const CREW_HOLIDAYS = '/crew-holidays';

export const createCrewHoliday = async (data: Record<string, unknown>) => {
  const response = await api.post(CREW_HOLIDAYS, data);
  mutate((key) => typeof key === 'string' && key.startsWith(CREW_HOLIDAYS));
  return response.data;
};

export const updateCrewHoliday = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`${CREW_HOLIDAYS}/${id}`, data);
  mutate(`${CREW_HOLIDAYS}/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith(CREW_HOLIDAYS));
  return response.data;
};

export const deleteCrewHoliday = async (id: number) => {
  await api.delete(`${CREW_HOLIDAYS}/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith(CREW_HOLIDAYS));
};

const CREW_QUESTIONNAIRE_KINDS = '/crew-questionnaires/kinds';
const CREW_QUESTIONNAIRES = '/crew-questionnaires/questionnaires';

export const createCrewQuestionnaireKind = async (data: Record<string, unknown>) => {
  const response = await api.post(CREW_QUESTIONNAIRE_KINDS, data);
  mutate((key) => typeof key === 'string' && key.startsWith(CREW_QUESTIONNAIRE_KINDS));
  return response.data;
};
export const updateCrewQuestionnaireKind = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`${CREW_QUESTIONNAIRE_KINDS}/${id}`, data);
  mutate(`${CREW_QUESTIONNAIRE_KINDS}/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith(CREW_QUESTIONNAIRE_KINDS));
  return response.data;
};
export const deleteCrewQuestionnaireKind = async (id: number) => {
  await api.delete(`${CREW_QUESTIONNAIRE_KINDS}/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith(CREW_QUESTIONNAIRE_KINDS));
};
export const createCrewQuestionnaire = async (data: Record<string, unknown>) => {
  const response = await api.post(CREW_QUESTIONNAIRES, data);
  mutate((key) => typeof key === 'string' && key.startsWith(CREW_QUESTIONNAIRES));
  return response.data;
};
export const updateCrewQuestionnaire = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`${CREW_QUESTIONNAIRES}/${id}`, data);
  mutate(`${CREW_QUESTIONNAIRES}/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith(CREW_QUESTIONNAIRES));
  return response.data;
};
export const deleteCrewQuestionnaire = async (id: number) => {
  await api.delete(`${CREW_QUESTIONNAIRES}/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith(CREW_QUESTIONNAIRES));
};

export const createMgaPeriod = async (data: Record<string, unknown>) => {
  const response = await api.post('/mga/periods', data);
  mutate((key) => typeof key === 'string' && key.startsWith('/mga/periods'));
  return response.data;
};

export const updateMgaPeriod = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/mga/periods/${id}`, data);
  mutate(`/mga/periods/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/mga/periods'));
  return response.data;
};

export const deleteMgaPeriod = async (id: number) => {
  await api.delete(`/mga/periods/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/mga/periods'));
};

export const generateMgaPeriod = async (id: number) => {
  const response = await api.post(`/mga/periods/${id}/generate`);
  mutate(`/mga/periods/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/mga/periods'));
  return response.data;
};

export const importMgaFromPrior = async (id: number) => {
  const response = await api.post(`/mga/periods/${id}/import-from-prior`);
  mutate(`/mga/periods/${id}`);
  mutate(`/mga/periods/${id}/bonded-lines`);
  mutate(`/mga/periods/${id}/cash-entries`);
  mutate((key) => typeof key === 'string' && key.startsWith('/mga/periods'));
  return response.data as {
    priorPeriod: { id: number; periodFrom: string; periodTo: string; status: string };
    previousBalancesUpdated: number;
    bondedStockCopied: number;
    openingCashCreated: number;
  };
};

export const updateMgaPeriodLines = async (id: number, data: { lines: unknown[] }) => {
  const response = await api.patch(`/mga/periods/${id}/lines`, data);
  mutate(`/mga/periods/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/mga/periods'));
  return response.data;
};

export const closeMgaPeriod = async (
  id: number,
  data: { masterApprovedByName: string },
) => {
  const response = await api.post(`/mga/periods/${id}/close`, data);
  mutate(`/mga/periods/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/mga/periods'));
  return response.data;
};

export const reopenMgaPeriod = async (id: number) => {
  const response = await api.post(`/mga/periods/${id}/reopen`);
  mutate(`/mga/periods/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/mga/periods'));
  return response.data;
};

export const createMgaCashEntry = async (periodId: number, data: Record<string, unknown>) => {
  const response = await api.post(`/mga/periods/${periodId}/cash-entries`, data);
  mutate(`/mga/periods/${periodId}/cash-entries`);
  return response.data;
};

export const updateMgaCashEntry = async (
  periodId: number,
  entryId: number,
  data: Record<string, unknown>,
) => {
  const response = await api.patch(`/mga/periods/${periodId}/cash-entries/${entryId}`, data);
  mutate(`/mga/periods/${periodId}/cash-entries`);
  return response.data;
};

export const deleteMgaCashEntry = async (periodId: number, entryId: number) => {
  await api.delete(`/mga/periods/${periodId}/cash-entries/${entryId}`);
  mutate(`/mga/periods/${periodId}/cash-entries`);
};

export const createMgaBondedLine = async (periodId: number, data: Record<string, unknown>) => {
  const response = await api.post(`/mga/periods/${periodId}/bonded-lines`, data);
  mutate(`/mga/periods/${periodId}/bonded-lines`);
  return response.data;
};

export const updateMgaBondedLine = async (
  periodId: number,
  lineId: number,
  data: Record<string, unknown>,
) => {
  const response = await api.patch(`/mga/periods/${periodId}/bonded-lines/${lineId}`, data);
  mutate(`/mga/periods/${periodId}/bonded-lines`);
  return response.data;
};

export const deleteMgaBondedLine = async (periodId: number, lineId: number) => {
  await api.delete(`/mga/periods/${periodId}/bonded-lines/${lineId}`);
  mutate(`/mga/periods/${periodId}/bonded-lines`);
};

export const generateMgaBondedFromPrior = async (periodId: number) => {
  const response = await api.post(`/mga/periods/${periodId}/bonded-lines/generate-from-prior`);
  mutate(`/mga/periods/${periodId}/bonded-lines`);
  return response.data;
};

export const createMgaCrewRequest = async (periodId: number, data: Record<string, unknown>) => {
  const response = await api.post(`/mga/periods/${periodId}/crew-requests`, data);
  mutate(`/mga/periods/${periodId}/crew-requests`);
  return response.data;
};

export const updateMgaCrewRequest = async (
  periodId: number,
  requestId: number,
  data: Record<string, unknown>,
) => {
  const response = await api.patch(`/mga/periods/${periodId}/crew-requests/${requestId}`, data);
  mutate(`/mga/periods/${periodId}/crew-requests`);
  return response.data;
};

export const deleteMgaCrewRequest = async (periodId: number, requestId: number) => {
  await api.delete(`/mga/periods/${periodId}/crew-requests/${requestId}`);
  mutate(`/mga/periods/${periodId}/crew-requests`);
};

export const openWrhDay = async (data: Record<string, unknown>) => {
  const response = await api.post('/wrh/days/open', data);
  mutate((key) => typeof key === 'string' && key.startsWith('/wrh/days'));
  return response.data;
};

export const ensureWrhSheetDay = async (data: { vesselId: number; workDate: string }) => {
  const response = await api.post('/wrh/sheet/ensure-day', data);
  return response.data as {
    vesselId: number;
    workDate: string;
    wrhDayId: number | null;
    created: boolean;
    generated: boolean;
    recomputed: boolean;
  };
};

export const seedWrhSheetDayArrangements = async (data: {
  vesselId: number;
  workDate: string;
}) => {
  const response = await api.post('/wrh/sheet/seed-day-arrangements', data);
  return response.data as {
    vesselId: number;
    workDate: string;
    wrhDayId: number | null;
    seededCount: number;
    skipped?: boolean;
  };
};

export const clearWrhSheetDayIntervals = async (data: {
  vesselId: number;
  workDate: string;
}) => {
  const response = await api.post('/wrh/sheet/clear-day-intervals', data);
  return response.data as {
    vesselId: number;
    workDate: string;
    wrhDayId: number | null;
    clearedCount: number;
    skipped?: boolean;
  };
};

export const createWrhDay = async (data: Record<string, unknown>) => {
  const response = await api.post('/wrh/days', data);
  mutate((key) => typeof key === 'string' && key.startsWith('/wrh/days'));
  return response.data;
};

export const deleteWrhDay = async (id: number) => {
  await api.delete(`/wrh/days/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/wrh/days'));
};

export const generateWrhDay = async (id: number) => {
  const response = await api.post(`/wrh/days/${id}/generate`);
  mutate(`/wrh/days/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/wrh/days'));
  return response.data;
};

export const seedWrhDayArrangements = async (id: number) => {
  const response = await api.post(`/wrh/days/${id}/seed-arrangements`);
  mutate(`/wrh/days/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/wrh/days'));
  mutate((key) => typeof key === 'string' && key.startsWith('/wrh/sheet'));
  return response.data;
};

export const updateWrhIntervals = async (
  dayId: number,
  crewDayId: number,
  data: { intervals: unknown[] },
) => {
  const response = await api.patch(`/wrh/days/${dayId}/crew/${crewDayId}/intervals`, data);
  mutate(`/wrh/days/${dayId}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/wrh/'));
  return response.data;
};

export const updateWrhCrewDay = async (
  dayId: number,
  crewDayId: number,
  data: Record<string, unknown>,
) => {
  const response = await api.patch(`/wrh/days/${dayId}/crew/${crewDayId}`, data);
  mutate(`/wrh/days/${dayId}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/wrh/'));
  return response.data;
};

export const closeWrhDay = async (id: number) => {
  const response = await api.post(`/wrh/days/${id}/close`);
  mutate(`/wrh/days/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/wrh/days'));
  return response.data;
};

export const reopenWrhDay = async (id: number) => {
  const response = await api.post(`/wrh/days/${id}/reopen`);
  mutate(`/wrh/days/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/wrh/days'));
  return response.data;
};

export const createWrhRuleSet = async (data: Record<string, unknown>) => {
  const response = await api.post('/wrh/rules', data);
  mutate((key) => typeof key === 'string' && key.startsWith('/wrh/rules'));
  return response.data;
};

export const updateWrhRuleSet = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/wrh/rules/${id}`, data);
  mutate((key) => typeof key === 'string' && key.startsWith('/wrh/rules'));
  return response.data;
};

export const activateWrhRuleSet = async (id: number) => {
  const response = await api.post(`/wrh/rules/${id}/activate`);
  mutate((key) => typeof key === 'string' && key.startsWith('/wrh/rules'));
  return response.data;
};

export const deleteWrhRuleSet = async (id: number) => {
  const response = await api.delete(`/wrh/rules/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/wrh/rules'));
  return response.data;
};

export const putWrhArrangements = async (data: Record<string, unknown>) => {
  const response = await api.put('/wrh/arrangements', data);
  mutate((key) => typeof key === 'string' && key.startsWith('/wrh/arrangements'));
  return response.data;
};

export const createVesselOperationType = async (data: Record<string, unknown>) => {
  const response = await api.post('/vessel-operation-types', data);
  mutate((key) => typeof key === 'string' && key.startsWith('/vessel-operation-types'));
  return response.data;
};

export const updateVesselOperationType = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/vessel-operation-types/${id}`, data);
  mutate(`/vessel-operation-types/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/vessel-operation-types'));
  return response.data;
};

export const setVesselOperationTypeRanks = async (id: number, rankIds: number[]) => {
  const response = await api.put(`/vessel-operation-types/${id}/ranks`, { rankIds });
  mutate(`/vessel-operation-types/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/vessel-operation-types'));
  return response.data;
};

export const deleteVesselOperationType = async (id: number) => {
  await api.delete(`/vessel-operation-types/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/vessel-operation-types'));
};

export const putVesselOperationDay = async (
  vesselId: number,
  workDate: string,
  data: Record<string, unknown>,
) => {
  const response = await api.put(`/vessel-operations/vessels/${vesselId}/days/${workDate}`, data);
  mutate((key) => typeof key === 'string' && key.startsWith('/vessel-operations'));
  mutate((key) => typeof key === 'string' && key.startsWith('/wrh/sheet'));
  return response.data;
};

const coerceCrewLookupPayload = (data: Record<string, unknown>) => {
  const out = { ...data };
  if (out.sortOrder === '' || out.sortOrder == null) out.sortOrder = 0;
  else out.sortOrder = Number(out.sortOrder);
  if (typeof out.code === 'string') {
    const c = out.code.trim();
    out.code = c ? c.toUpperCase() : null;
  }
  for (const key of ['abrv', 'phone', 'email', 'description'] as const) {
    if (out[key] === '') out[key] = null;
  }
  if (out.categoryId === '' || out.categoryId === undefined) {
    // leave undefined on update omit; empty string → null for clear
    if (out.categoryId === '') out.categoryId = null;
  } else if (out.categoryId != null) {
    out.categoryId = Number(out.categoryId);
  }
  return out;
};

// --- Spares module mutations (generated from supplies) ---
const coerceSpareCasePayload = (data: Record<string, unknown>) => {
  const out = { ...data };
  const nullableNumberFields = ['vesselId', 'statusId', 'portId'];

  for (const field of nullableNumberFields) {
    const value = out[field];
    if (value === '' || value === null || value === undefined) {
      out[field] = null;
    } else {
      out[field] = Number(value);
    }
  }

  const nullableDateFields = ['caseDate', 'eta'];
  for (const field of nullableDateFields) {
    const value = out[field];
    if (value === '' || value === null || value === undefined) {
      out[field] = null;
    }
  }

  if (Array.isArray(out.categoryIds)) {
    out.categoryIds = (out.categoryIds as unknown[])
      .map((x) => {
        if (x === '' || x === null || x === undefined) return null;
        const n = Number(x);
        return Number.isNaN(n) ? null : n;
      })
      .filter((x): x is number => x !== null);
  }

  return out;
};

/** @deprecated Prefer peekReference — same unified API. */
export const peekSpareReference = peekReference;

export const createSpareCase = async (data: Record<string, unknown>) => {
  const response = await api.post('/spare-cases', coerceSpareCasePayload(data));
  mutate('/spare-cases');
  return response.data;
};

export const updateSpareCase = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/spare-cases/${id}`, coerceSpareCasePayload(data));
  mutate(`/spare-cases/${id}`);
  mutate('/spare-cases');
  return response.data;
};

export const deleteSpareCase = async (id: number) => {
  await api.delete(`/spare-cases/${id}`);
  mutate('/spare-cases');
};

const coerceSpareRequisitionPayload = (data: Record<string, unknown>) => {
  const out = { ...data } as Record<string, unknown>;
  const nullableNumberFields = ['caseId', 'vesselId', 'refN'];
  for (const field of nullableNumberFields) {
    const value = out[field];
    if (value === '' || value === null || value === undefined) {
      out[field] = null;
    } else {
      out[field] = Number(value);
    }
  }

  if (Array.isArray(out.categoryIds)) {
    out.categoryIds = (out.categoryIds as unknown[])
      .map((x) => {
        if (x === '' || x === null || x === undefined) return null;
        const n = Number(x);
        return Number.isNaN(n) ? null : n;
      })
      .filter((x): x is number => x !== null);
  }

  if (Array.isArray(out.lines)) {
    out.lines = (out.lines as Record<string, unknown>[]).map((line) => {
      const next = { ...line };
      for (const field of ['itemId', 'qty']) {
        const value = next[field];
        if (value === '' || value === null || value === undefined) {
          next[field] = null;
        } else {
          next[field] = Number(value);
        }
      }
      delete next.unitId;
      delete next.unit;
      delete next.rob;
      return next;
    });
  }

  return out;
};

export const createSpareRequisition = async (data: Record<string, unknown>) => {
  const response = await api.post('/spare-requisitions', coerceSpareRequisitionPayload(data));
  mutate((key) => typeof key === 'string' && key.startsWith('/spare-requisitions'), undefined, {
    revalidate: true,
  });
  return response.data;
};

export type SpareRequisitionExcelImportResult = {
  ref: string | null;
  reqDate: string | null;
  vesselId: number | null;
  vesselNameRaw: string | null;
  categoryIds: number[];
  categoryNameRaw: string | null;
  notes: string | null;
  lineCount: number;
  warnings: string[];
  lines: Array<{
    itemVcode?: string | null;
    itemDescr?: string | null;
    unit?: string | null;
    unitUcodeRaw?: string | null;
    unitMatched: boolean;
    qty?: number | null;
    rob?: number | null;
    itemNotes?: string | null;
    itemId?: number | null;
    level1Filter?: string;
    categoryFilter?: string;
  }>;
};

/** Parses .xlsx using the active REQUISITION Excel document template mapping. */
export const importSpareRequisitionFromExcel = async (
  file: File,
): Promise<SpareRequisitionExcelImportResult> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post<SpareRequisitionExcelImportResult>(
    '/spare-requisitions/import-excel',
    formData,
  );
  return response.data;
};

export const updateSpareRequisition = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/spare-requisitions/${id}`, coerceSpareRequisitionPayload(data));
  mutate(`/spare-requisitions/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/spare-requisitions'), undefined, {
    revalidate: true,
  });
  return response.data;
};

/** New draft requisition with same lines/header fields (case and refs cleared). */
export const duplicateSpareRequisition = async (id: number) => {
  const response = await api.post(`/spare-requisitions/${id}/duplicate`);
  mutate((key) => typeof key === 'string' && key.startsWith('/spare-requisitions'), undefined, {
    revalidate: true,
  });
  return response.data;
};

export const createSpareRequisitionTemplate = async (data: { name: string; lines: Record<string, unknown>[] }) => {
  const response = await api.post('/spare-requisition-templates', data);
  mutate('/spare-requisition-templates');
  return response.data;
};

/** Permanently deletes the requisition and its lines (blocked when RFQ / quotation / PO is linked). */
export const deleteSpareRequisition = async (id: number) => {
  await api.delete(`/spare-requisitions/${id}`);
  mutate(`/spare-requisitions/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/spare-requisitions'), undefined, {
    revalidate: true,
  });
};

/** Clears `caseId` only; requisition remains (use from supply case custom view). */
export const unlinkSpareRequisitionFromCase = async (id: number) => {
  await api.post(`/spare-requisitions/${id}/unlink-from-case`);
  mutate(`/spare-requisitions/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/spare-requisitions'), undefined, {
    revalidate: true,
  });
};

const coerceSpareSupplierEnquiryPayload = (data: Record<string, unknown>) => {
  const out = { ...data } as Record<string, unknown>;
  for (const field of ['caseId', 'supplierId', 'requisitionId']) {
    const value = out[field];
    if (value === '' || value === null || value === undefined) {
      out[field] = null;
    } else {
      out[field] = Number(value);
    }
  }

  if (Array.isArray(out.lines)) {
    out.lines = (out.lines as Record<string, unknown>[]).map((line) => {
      const next = { ...line };
      for (const field of ['itemId', 'unitId', 'qty', 'requisitionQty']) {
        const v = next[field];
        if (v === '' || v === null || v === undefined) {
          next[field] = null;
        } else {
          next[field] = Number(v);
        }
      }
      return next;
    });
  }

  return out;
};

export const createSpareSupplierEnquiry = async (data: Record<string, unknown>) => {
  const response = await api.post('/spare-supplier-enquiries', coerceSpareSupplierEnquiryPayload(data));
  mutate('/spare-supplier-enquiries');
  return response.data;
};

export const updateSpareSupplierEnquiry = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/spare-supplier-enquiries/${id}`, coerceSpareSupplierEnquiryPayload(data));
  mutate(`/spare-supplier-enquiries/${id}`);
  mutate('/spare-supplier-enquiries');
  return response.data;
};

export const deleteSpareSupplierEnquiry = async (id: number) => {
  await api.delete(`/spare-supplier-enquiries/${id}`);
  mutate('/spare-supplier-enquiries');
};

export const createSpareSupplierEnquiryFromRequisition = async (data: {
  requisitionId: number;
  supplierId: number;
  enqDate?: string;
  ref?: string;
  notes?: string;
}) => {
  const response = await api.post('/spare-supplier-enquiries/from-requisition', data);
  mutate('/spare-supplier-enquiries');
  return response.data;
};

export const createSpareSupplierEnquiryFromRequisitionMulti = async (data: {
  requisitionId: number;
  supplierIds: number[];
  enqDate?: string;
  ref?: string;
  notes?: string;
}) => {
  const response = await api.post('/spare-supplier-enquiries/from-requisition-multi', data);
  mutate('/spare-supplier-enquiries');
  return response.data as Array<{ id: number }>;
};

export const createSpareSupplierEnquiryRfqLink = async (
  enquiryId: number,
  body: { pin: string; expiresInDays: number },
) => {
  const response = await api.post(`/spare-supplier-enquiries/${enquiryId}/rfq-link`, body);
  mutate(`/spare-supplier-enquiries/${enquiryId}/rfq-link`);
  mutate(`/spare-supplier-enquiries/${enquiryId}`);
  return response.data as { token: string; urlPath: string; fullUrl?: string };
};

export const revokeSpareSupplierEnquiryRfqLink = async (enquiryId: number) => {
  await api.delete(`/spare-supplier-enquiries/${enquiryId}/rfq-link`);
  mutate(`/spare-supplier-enquiries/${enquiryId}/rfq-link`);
  mutate(`/spare-supplier-enquiries/${enquiryId}`);
};

export const downloadSpareSupplierEnquiryRfqTemplate = async (
  enquiryId: number,
): Promise<{ usedFallback: boolean }> => {
  const response = await api.get(`/spare-supplier-enquiries/${enquiryId}/rfq-template`, {
    responseType: 'blob',
  });
  const fallbackRaw =
    response.headers['x-export-template-fallback'] ?? response.headers['X-Export-Template-Fallback'];
  const usedFallback = fallbackRaw === '1' || String(fallbackRaw).toLowerCase() === 'true';
  const cd = response.headers['content-disposition'];
  let filename = `RFQ-${enquiryId}-template.xlsx`;
  if (cd && typeof cd === 'string') {
    const m = /filename="?([^";]+)"?/i.exec(cd);
    if (m?.[1]) filename = m[1];
  }
  const url = window.URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
  return { usedFallback };
};


export const importSpareSupplierEnquiryRfqExcel = async (enquiryId: number, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post(`/spare-supplier-enquiries/${enquiryId}/rfq-import`, formData);
  mutate(`/spare-supplier-enquiries/${enquiryId}`);
  mutate('/spare-supplier-quotations');
  return response.data;
};

/** @deprecated Prefer upsertReferenceTemplate */
export const upsertSpareReferenceTemplate = upsertReferenceTemplate;
/** @deprecated Prefer patchReferenceTemplateAllowManualEdit */
export const patchSpareReferenceTemplateAllowManualEdit = patchReferenceTemplateAllowManualEdit;

const coerceSpareSupplierQuotationPayload = (data: Record<string, unknown>) => {
  const out = { ...data } as Record<string, unknown>;
  for (const field of ['enqId', 'currencyId', 'rate']) {
    const value = out[field];
    if (value === '' || value === null || value === undefined) {
      out[field] = null;
    } else {
      out[field] = Number(value);
    }
  }

  if (Array.isArray(out.lineItems)) {
    out.lineItems = (out.lineItems as Record<string, unknown>[]).map((line) => {
      const next = { ...line };
      for (const field of [
        'itemId',
        'unitId',
        'qty',
        'unitPrice',
        'lineTotal',
        'discount',
        'vat',
        'requisitionQty',
        'enquiryQty',
      ]) {
        const v = next[field];
        if (v === '' || v === null || v === undefined) {
          next[field] = null;
        } else {
          next[field] = Number(v);
        }
      }
      return next;
    });
  }

  if (Array.isArray(out.expenses)) {
    out.expenses = (out.expenses as Record<string, unknown>[]).map((row) => {
      const next = { ...row };
      for (const field of ['value', 'vat']) {
        const v = next[field];
        if (v === '' || v === null || v === undefined) {
          next[field] = null;
        } else {
          next[field] = Number(v);
        }
      }
      return next;
    });
  }

  return out;
};

export const createSpareSupplierQuotation = async (data: Record<string, unknown>) => {
  const response = await api.post('/spare-supplier-quotations', coerceSpareSupplierQuotationPayload(data));
  mutate('/spare-supplier-quotations');
  return response.data;
};

export const updateSpareSupplierQuotation = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/spare-supplier-quotations/${id}`, coerceSpareSupplierQuotationPayload(data));
  mutate(`/spare-supplier-quotations/${id}`);
  mutate('/spare-supplier-quotations');
  return response.data;
};

export const deleteSpareSupplierQuotation = async (id: number) => {
  await api.delete(`/spare-supplier-quotations/${id}`);
  mutate('/spare-supplier-quotations');
};

export const createSpareSupplierQuotationFromEnquiry = async (data: {
  enquiryId: number;
  quoDate?: string;
  ref?: string;
  notes?: string;
}) => {
  const response = await api.post('/spare-supplier-quotations/from-enquiry', data);
  mutate('/spare-supplier-quotations');
  return response.data;
};

const coerceSpareInvoicePayload = (data: Record<string, unknown>): Record<string, unknown> => {
  const out = { ...data } as Record<string, unknown>;
  for (const field of ['purchaseOrderId', 'currencyId', 'refN', 'deliveryId', 'vesselId']) {
    const v = out[field];
    if (v === '' || v === null || v === undefined) {
      out[field] = v === undefined ? v : null;
    } else {
      out[field] = Number(v);
    }
  }
  if (out.exchangeRate === '' || out.exchangeRate === null || out.exchangeRate === undefined) {
    out.exchangeRate = null;
  } else {
    out.exchangeRate = Number(out.exchangeRate);
  }
  if (Array.isArray(out.lineItems)) {
    out.lineItems = (out.lineItems as Record<string, unknown>[]).map((line) => {
      const next = { ...line };
      for (const field of [
        'itemId',
        'unitId',
        'invoiceQty',
        'unitPrice',
        'lineTotal',
        'discount',
        'vat',
        'requisitionQty',
        'enquiryQty',
        'quotationQty',
        'deliveryQty',
        'purchaseOrderQty',
      ]) {
        const n = next[field];
        if (n === '' || n === null || n === undefined) {
          next[field] = null;
        } else {
          next[field] = Number(n);
        }
      }
      return next;
    });
  }
  if (Array.isArray(out.expenses)) {
    out.expenses = (out.expenses as Record<string, unknown>[]).map((row) => {
      const next = { ...row };
      for (const field of ['value', 'vat']) {
        const n = next[field];
        if (n === '' || n === null || n === undefined) {
          next[field] = null;
        } else {
          next[field] = Number(n);
        }
      }
      return next;
    });
  }
  return out;
};

export const createSpareInvoice = async (data: Record<string, unknown>) => {
  const response = await api.post('/spare-invoices', coerceSpareInvoicePayload(data));
  mutate('/spare-invoices');
  return response.data;
};

export const updateSpareInvoice = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/spare-invoices/${id}`, coerceSpareInvoicePayload(data));
  mutate(`/spare-invoices/${id}`);
  mutate('/spare-invoices');
  return response.data;
};

export const deleteSpareInvoice = async (id: number) => {
  await api.delete(`/spare-invoices/${id}`);
  mutate('/spare-invoices');
};

export const createSpareInvoiceFromPurchaseOrder = async (data: {
  purchaseOrderId: number;
  invDate?: string;
  ref?: string;
  notes?: string;
  deliveryId?: number;
  vesselId?: number;
}) => {
  const response = await api.post('/spare-invoices/from-purchase-order', data);
  mutate('/spare-invoices');
  return response.data;
};

const coerceSparePurchaseOrderPayload = (data: Record<string, unknown>) => {
  const out = { ...data } as Record<string, unknown>;
  for (const field of ['quotationId', 'currencyId', 'exchangeRate']) {
    const value = out[field];
    if (value === '' || value === null || value === undefined) {
      out[field] = null;
    } else {
      out[field] = Number(value);
    }
  }

  if (Array.isArray(out.lineItems)) {
    out.lineItems = (out.lineItems as Record<string, unknown>[]).map((line) => {
      const next = { ...line };
      for (const field of [
        'itemId',
        'unitId',
        'qty',
        'unitPrice',
        'lineTotal',
        'discount',
        'vat',
        'requisitionQty',
        'enquiryQty',
        'quotationQty',
      ]) {
        const v = next[field];
        if (v === '' || v === null || v === undefined) {
          next[field] = null;
        } else {
          next[field] = Number(v);
        }
      }
      return next;
    });
  }

  if (Array.isArray(out.expenses)) {
    out.expenses = (out.expenses as Record<string, unknown>[]).map((row) => {
      const next = { ...row };
      for (const field of ['value', 'vat']) {
        const v = next[field];
        if (v === '' || v === null || v === undefined) {
          next[field] = null;
        } else {
          next[field] = Number(v);
        }
      }
      return next;
    });
  }

  return out;
};

export const createSparePurchaseOrder = async (data: Record<string, unknown>) => {
  const response = await api.post('/spare-purchase-orders', coerceSparePurchaseOrderPayload(data));
  mutate('/spare-purchase-orders');
  return response.data;
};

export const updateSparePurchaseOrder = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/spare-purchase-orders/${id}`, coerceSparePurchaseOrderPayload(data));
  mutate(`/spare-purchase-orders/${id}`);
  mutate('/spare-purchase-orders');
  return response.data;
};

export const deleteSparePurchaseOrder = async (id: number) => {
  await api.delete(`/spare-purchase-orders/${id}`);
  mutate('/spare-purchase-orders');
};

export const createSparePurchaseOrderFromQuotation = async (data: {
  quotationId: number;
  orderDate?: string;
  ref?: string;
  notes?: string;
}) => {
  const response = await api.post('/spare-purchase-orders/from-quotation', data);
  mutate('/spare-purchase-orders');
  return response.data;
};

const coerceSpareDeliveryPayload = (data: Record<string, unknown>) => {
  const out = { ...data } as Record<string, unknown>;
  for (const field of ['vesselId', 'caseId', 'supplierId', 'portCode', 'refN', 'spareForwardingId']) {
    const value = out[field];
    if (value === '' || value === null || value === undefined) {
      if (field === 'portCode' || field === 'refN' || field === 'spareForwardingId') {
        delete out[field];
      }
    } else {
      out[field] = Number(value);
    }
  }
  const poid = out.purchaseOrderId;
  if (poid === '' || poid === null || poid === undefined) {
    out.purchaseOrderId = null;
  } else {
    out.purchaseOrderId = Number(poid);
  }
  if (Array.isArray(out.lines)) {
    out.lines = (out.lines as Record<string, unknown>[]).map((line) => {
      const next = { ...line };
      for (const field of ['caseId', 'itemId', 'unitId', 'purchaseOrderQty', 'deliveryQty']) {
        const v = next[field];
        if (v === '' || v === null || v === undefined) {
          next[field] = null;
        } else {
          next[field] = Number(v);
        }
      }
      if (next.isDelivered === '' || next.isDelivered === null || next.isDelivered === undefined) {
        next.isDelivered = false;
      } else {
        next.isDelivered = Boolean(next.isDelivered);
      }
      return next;
    });
  }
  return out;
};

export const createSpareDelivery = async (data: Record<string, unknown>) => {
  const response = await api.post('/spare-deliveries', coerceSpareDeliveryPayload(data));
  mutate('/spare-deliveries');
  return response.data;
};

export const createSpareDeliveryFromPurchaseOrder = async (data: Record<string, unknown>) => {
  const response = await api.post('/spare-deliveries/from-purchase-order', coerceSpareDeliveryPayload(data));
  mutate('/spare-deliveries');
  mutate((key) => typeof key === 'string' && key.includes('/spare-purchase-orders/pending-for-vessel'));
  const fwd = data?.spareForwardingId;
  if (fwd != null && fwd !== '') {
    const n = Number(fwd);
    if (Number.isFinite(n)) mutate(`/spare-forwardings/${n}`);
  }
  return response.data;
};

export const updateSpareDelivery = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/spare-deliveries/${id}`, coerceSpareDeliveryPayload(data));
  mutate(`/spare-deliveries/${id}`);
  mutate('/spare-deliveries');
  return response.data;
};

export const deleteSpareDelivery = async (id: number) => {
  await api.delete(`/spare-deliveries/${id}`);
  mutate('/spare-deliveries');
};

export const createSpareForwardingStatus = async (data: Record<string, unknown>) => {
  const response = await api.post('/spare-forwarding-statuses', coerceSupplyForwardingStatusPayload(data));
  mutate('/spare-forwarding-statuses');
  return response.data;
};

export const updateSpareForwardingStatus = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/spare-forwarding-statuses/${id}`, coerceSupplyForwardingStatusPayload(data));
  mutate(`/spare-forwarding-statuses/${id}`);
  mutate('/spare-forwarding-statuses');
  return response.data;
};

export const deleteSpareForwardingStatus = async (id: number) => {
  await api.delete(`/spare-forwarding-statuses/${id}`);
  mutate('/spare-forwarding-statuses');
};

const coerceSpareForwardingPayload = (data: Record<string, unknown>) => {
  const out = { ...data };
  for (const key of [
    'caseId',
    'purchaseOrderId',
    'forwarderId',
    'portId',
    'statusId',
    'priorityId',
    'warehouseLocationId',
    'packageCount',
  ]) {
    const v = out[key];
    if (v === '' || v === undefined) {
      out[key] = null;
    } else if (v != null) {
      out[key] = Number(v);
    }
  }
  for (const key of [
    'costUsd',
    'totalWeight',
    'warehouseCostUsd',
    'subAgentCostUsd',
    'vesselAgentCostUsd',
  ]) {
    const v = out[key];
    if (v === '' || v === undefined) {
      out[key] = null;
    } else if (v != null) {
      out[key] = Number(v);
    }
  }
  return out;
};

export const createSpareForwarding = async (data: Record<string, unknown>) => {
  const response = await api.post('/spare-forwardings', coerceSpareForwardingPayload(data));
  mutate('/spare-forwardings');
  return response.data;
};

export const updateSpareForwarding = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/spare-forwardings/${id}`, coerceSpareForwardingPayload(data));
  mutate(`/spare-forwardings/${id}`);
  mutate('/spare-forwardings');
  return response.data;
};

export const deleteSpareForwarding = async (id: number) => {
  await api.delete(`/spare-forwardings/${id}`);
  mutate('/spare-forwardings');
};

export const createSpareQuotationEvaluation = async (data: {
  caseId: number;
  quotationIds: number[];
  evaluationDate?: string;
  ref?: string;
  notes?: string;
}) => {
  const response = await api.post('/spare-quotation-evaluations', data);
  mutate((key) => typeof key === 'string' && key.startsWith('/spare-quotation-evaluations'));
  mutate((key) => typeof key === 'string' && key.includes('/case-view'));
  return response.data as { id: number };
};

export const updateSpareQuotationEvaluation = async (
  id: number,
  data: Partial<{
    quotationIds: number[];
    evaluationDate: string | null;
    ref: string | null;
    notes: string | null;
  }>,
) => {
  const response = await api.patch(`/spare-quotation-evaluations/${id}`, data);
  mutate(`/spare-quotation-evaluations/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/spare-quotation-evaluations'));
  mutate((key) => typeof key === 'string' && key.includes('/case-view'));
  return response.data;
};

export const deleteSpareQuotationEvaluation = async (id: number) => {
  await api.delete(`/spare-quotation-evaluations/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/spare-quotation-evaluations'));
  mutate((key) => typeof key === 'string' && key.includes('/case-view'));
};


const coerceSpareLibraryPayload = (data: Record<string, unknown>) => {
  const out = { ...data };
  const n = out.sortOrder;
  if (n === '' || n === null || n === undefined) {
    out.sortOrder = 0;
  } else {
    out.sortOrder = Number(n);
  }
  return out;
};

export const createSpareLibrary = async (data: Record<string, unknown>) => {
  const response = await api.post('/spare-libraries', coerceSpareLibraryPayload(data));
  mutate('/spare-libraries');
  return response.data;
};

export const updateSpareLibrary = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/spare-libraries/${id}`, coerceSpareLibraryPayload(data));
  mutate(`/spare-libraries/${id}`);
  mutate('/spare-libraries');
  return response.data;
};

export const deleteSpareLibrary = async (id: number) => {
  await api.delete(`/spare-libraries/${id}`);
  mutate('/spare-libraries');
};

const coerceSpareItemPayload = (data: Record<string, unknown>) => {
  const out = { ...data };
  for (const field of ['libraryId', 'categoryId', 'stockMin', 'stockMax']) {
    const value = out[field];
    if (value === '' || value === null || value === undefined) {
      out[field] = null;
    } else {
      out[field] = Number(value);
    }
  }
  for (const field of ['drawingNo', 'serialNo', 'vendorCode'] as const) {
    if (typeof out[field] === 'string') {
      out[field] = (out[field] as string).trim() || null;
    }
  }
  for (const field of ['isActive', 'isCritical', 'isHazardousMaterial'] as const) {
    if (out[field] !== undefined && out[field] !== null) {
      out[field] = Boolean(out[field]);
    }
  }
  return out;
};

export const createSpareItem = async (
  data: Record<string, unknown>,
  files?: File[],
  _filesToDetach?: number[],
) => {
  const response = await api.post('/spare-items', coerceSpareItemPayload(data));
  const created = response.data as { id: number };
  if (files?.length && created?.id) {
    await uploadSpareItemFiles(created.id, files);
  }
  mutate('/spare-items');
  if (created?.id) mutate(`/spare-items/${created.id}`);
  return response.data;
};

export const updateSpareItem = async (
  id: number,
  data: Record<string, unknown>,
  files?: File[],
  filesToDetach?: number[],
) => {
  const response = await api.patch(`/spare-items/${id}`, coerceSpareItemPayload(data));
  if (filesToDetach?.length) {
    for (const fileId of filesToDetach) {
      await deleteSpareItemFile(id, fileId);
    }
  }
  if (files?.length) {
    await uploadSpareItemFiles(id, files);
  }
  mutate(`/spare-items/${id}`);
  mutate('/spare-items');
  return response.data;
};

export const deleteSpareItem = async (id: number) => {
  await api.delete(`/spare-items/${id}`);
  mutate('/spare-items');
};

export const uploadSpareItemFiles = async (id: number, files: File[]) => {
  const formData = new FormData();
  files.forEach((f) => formData.append('files', f));
  const response = await api.post(`/spare-items/${id}/files`, formData);
  mutate(`/spare-items/${id}`);
  mutate('/spare-items');
  return response.data;
};

export const deleteSpareItemFile = async (itemId: number, fileId: number) => {
  await api.delete(`/spare-items/${itemId}/files/${fileId}`);
  mutate(`/spare-items/${itemId}`);
  mutate('/spare-items');
};

const coerceSpareCategoryPayload = (data: Record<string, unknown>) => {
  const out = { ...data };
  for (const field of ['libraryId', 'pid', 'sortOrder']) {
    const value = out[field];
    if (value === '' || value === null || value === undefined) {
      out[field] = field === 'pid' ? null : field === 'sortOrder' ? 0 : null;
    } else {
      out[field] = Number(value);
    }
  }
  return out;
};

export const createSpareCategory = async (data: Record<string, unknown>) => {
  const response = await api.post('/spare-categories', coerceSpareCategoryPayload(data));
  mutate('/spare-categories');
  return response.data;
};

export const updateSpareCategory = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/spare-categories/${id}`, coerceSpareCategoryPayload(data));
  mutate(`/spare-categories/${id}`);
  mutate('/spare-categories');
  return response.data;
};

export const deleteSpareCategory = async (id: number) => {
  await api.delete(`/spare-categories/${id}`);
  mutate('/spare-categories');
};
// --- PMS lookups ---

export const createPmsRecordStatus = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-record-statuses', data);
  mutate('/pms-record-statuses');
  return response.data;
};

export const updatePmsRecordStatus = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-record-statuses/${id}`, data);
  mutate(`/pms-record-statuses/${id}`);
  mutate('/pms-record-statuses');
  return response.data;
};

export const deletePmsRecordStatus = async (id: number) => {
  await api.delete(`/pms-record-statuses/${id}`);
  mutate('/pms-record-statuses');
};

export const createPmsCriticality = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-criticalities', data);
  mutate('/pms-criticalities');
  return response.data;
};

export const updatePmsCriticality = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-criticalities/${id}`, data);
  mutate(`/pms-criticalities/${id}`);
  mutate('/pms-criticalities');
  return response.data;
};

export const deletePmsCriticality = async (id: number) => {
  await api.delete(`/pms-criticalities/${id}`);
  mutate('/pms-criticalities');
};

export const createPmsEquipmentStatus = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-equipment-statuses', data);
  mutate('/pms-equipment-statuses');
  return response.data;
};

export const updatePmsEquipmentStatus = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-equipment-statuses/${id}`, data);
  mutate(`/pms-equipment-statuses/${id}`);
  mutate('/pms-equipment-statuses');
  return response.data;
};

export const deletePmsEquipmentStatus = async (id: number) => {
  await api.delete(`/pms-equipment-statuses/${id}`);
  mutate('/pms-equipment-statuses');
};

export const createPmsMaintenanceStrategy = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-maintenance-strategies', data);
  mutate('/pms-maintenance-strategies');
  return response.data;
};

export const updatePmsMaintenanceStrategy = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-maintenance-strategies/${id}`, data);
  mutate(`/pms-maintenance-strategies/${id}`);
  mutate('/pms-maintenance-strategies');
  return response.data;
};

export const deletePmsMaintenanceStrategy = async (id: number) => {
  await api.delete(`/pms-maintenance-strategies/${id}`);
  mutate('/pms-maintenance-strategies');
};

export const createPmsJobType = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-job-types', data);
  mutate('/pms-job-types');
  return response.data;
};

export const updatePmsJobType = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-job-types/${id}`, data);
  mutate(`/pms-job-types/${id}`);
  mutate('/pms-job-types');
  return response.data;
};

export const deletePmsJobType = async (id: number) => {
  await api.delete(`/pms-job-types/${id}`);
  mutate('/pms-job-types');
};

export const createPmsScheduleType = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-schedule-types', data);
  mutate('/pms-schedule-types');
  return response.data;
};

export const updatePmsScheduleType = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-schedule-types/${id}`, data);
  mutate(`/pms-schedule-types/${id}`);
  mutate('/pms-schedule-types');
  return response.data;
};

export const deletePmsScheduleType = async (id: number) => {
  await api.delete(`/pms-schedule-types/${id}`);
  mutate('/pms-schedule-types');
};

export const createPmsIntervalUnit = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-interval-units', data);
  mutate('/pms-interval-units');
  return response.data;
};

export const updatePmsIntervalUnit = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-interval-units/${id}`, data);
  mutate(`/pms-interval-units/${id}`);
  mutate('/pms-interval-units');
  return response.data;
};

export const deletePmsIntervalUnit = async (id: number) => {
  await api.delete(`/pms-interval-units/${id}`);
  mutate('/pms-interval-units');
};

export const createPmsWorkOrderStatus = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-work-order-statuses', data);
  mutate('/pms-work-order-statuses');
  return response.data;
};

export const updatePmsWorkOrderStatus = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-work-order-statuses/${id}`, data);
  mutate(`/pms-work-order-statuses/${id}`);
  mutate('/pms-work-order-statuses');
  return response.data;
};

export const deletePmsWorkOrderStatus = async (id: number) => {
  await api.delete(`/pms-work-order-statuses/${id}`);
  mutate('/pms-work-order-statuses');
};

export const createPmsWorkOrderSource = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-work-order-sources', data);
  mutate('/pms-work-order-sources');
  return response.data;
};

export const updatePmsWorkOrderSource = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-work-order-sources/${id}`, data);
  mutate(`/pms-work-order-sources/${id}`);
  mutate('/pms-work-order-sources');
  return response.data;
};

export const deletePmsWorkOrderSource = async (id: number) => {
  await api.delete(`/pms-work-order-sources/${id}`);
  mutate('/pms-work-order-sources');
};

export const createPmsApprovalStatus = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-approval-statuses', data);
  mutate('/pms-approval-statuses');
  return response.data;
};

export const updatePmsApprovalStatus = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-approval-statuses/${id}`, data);
  mutate(`/pms-approval-statuses/${id}`);
  mutate('/pms-approval-statuses');
  return response.data;
};

export const deletePmsApprovalStatus = async (id: number) => {
  await api.delete(`/pms-approval-statuses/${id}`);
  mutate('/pms-approval-statuses');
};

export const createPmsStepStatus = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-step-statuses', data);
  mutate('/pms-step-statuses');
  return response.data;
};

export const updatePmsStepStatus = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-step-statuses/${id}`, data);
  mutate(`/pms-step-statuses/${id}`);
  mutate('/pms-step-statuses');
  return response.data;
};

export const deletePmsStepStatus = async (id: number) => {
  await api.delete(`/pms-step-statuses/${id}`);
  mutate('/pms-step-statuses');
};

export const createPmsCounterType = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-counter-types', data);
  mutate('/pms-counter-types');
  return response.data;
};

export const updatePmsCounterType = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-counter-types/${id}`, data);
  mutate(`/pms-counter-types/${id}`);
  mutate('/pms-counter-types');
  return response.data;
};

export const deletePmsCounterType = async (id: number) => {
  await api.delete(`/pms-counter-types/${id}`);
  mutate('/pms-counter-types');
};

export const createPmsCounterSource = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-counter-sources', data);
  mutate('/pms-counter-sources');
  return response.data;
};

export const updatePmsCounterSource = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-counter-sources/${id}`, data);
  mutate(`/pms-counter-sources/${id}`);
  mutate('/pms-counter-sources');
  return response.data;
};

export const deletePmsCounterSource = async (id: number) => {
  await api.delete(`/pms-counter-sources/${id}`);
  mutate('/pms-counter-sources');
};

export const createPmsMeasurementResult = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-measurement-results', data);
  mutate('/pms-measurement-results');
  return response.data;
};

export const updatePmsMeasurementResult = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-measurement-results/${id}`, data);
  mutate(`/pms-measurement-results/${id}`);
  mutate('/pms-measurement-results');
  return response.data;
};

export const deletePmsMeasurementResult = async (id: number) => {
  await api.delete(`/pms-measurement-results/${id}`);
  mutate('/pms-measurement-results');
};

export const createPmsDefectSeverity = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-defect-severities', data);
  mutate('/pms-defect-severities');
  return response.data;
};

export const updatePmsDefectSeverity = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-defect-severities/${id}`, data);
  mutate(`/pms-defect-severities/${id}`);
  mutate('/pms-defect-severities');
  return response.data;
};

export const deletePmsDefectSeverity = async (id: number) => {
  await api.delete(`/pms-defect-severities/${id}`);
  mutate('/pms-defect-severities');
};

export const createPmsDefectStatus = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-defect-statuses', data);
  mutate('/pms-defect-statuses');
  return response.data;
};

export const updatePmsDefectStatus = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-defect-statuses/${id}`, data);
  mutate(`/pms-defect-statuses/${id}`);
  mutate('/pms-defect-statuses');
  return response.data;
};

export const deletePmsDefectStatus = async (id: number) => {
  await api.delete(`/pms-defect-statuses/${id}`);
  mutate('/pms-defect-statuses');
};

export const createPmsEquipmentInstanceStatus = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-equipment-instance-statuses', data);
  mutate('/pms-equipment-instance-statuses');
  return response.data;
};

export const updatePmsEquipmentInstanceStatus = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-equipment-instance-statuses/${id}`, data);
  mutate(`/pms-equipment-instance-statuses/${id}`);
  mutate('/pms-equipment-instance-statuses');
  return response.data;
};

export const deletePmsEquipmentInstanceStatus = async (id: number) => {
  await api.delete(`/pms-equipment-instance-statuses/${id}`);
  mutate('/pms-equipment-instance-statuses');
};

export const createPmsSafetyRequirementType = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-safety-requirement-types', data);
  mutate('/pms-safety-requirement-types');
  return response.data;
};

export const updatePmsSafetyRequirementType = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-safety-requirement-types/${id}`, data);
  mutate(`/pms-safety-requirement-types/${id}`);
  mutate('/pms-safety-requirement-types');
  return response.data;
};

export const deletePmsSafetyRequirementType = async (id: number) => {
  await api.delete(`/pms-safety-requirement-types/${id}`);
  mutate('/pms-safety-requirement-types');
};

export const createPmsDocumentType = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-document-types', data);
  mutate('/pms-document-types');
  return response.data;
};

export const updatePmsDocumentType = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-document-types/${id}`, data);
  mutate(`/pms-document-types/${id}`);
  mutate('/pms-document-types');
  return response.data;
};

export const deletePmsDocumentType = async (id: number) => {
  await api.delete(`/pms-document-types/${id}`);
  mutate('/pms-document-types');
};

export const createPmsVerificationStatus = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-verification-statuses', data);
  mutate('/pms-verification-statuses');
  return response.data;
};

export const updatePmsVerificationStatus = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-verification-statuses/${id}`, data);
  mutate(`/pms-verification-statuses/${id}`);
  mutate('/pms-verification-statuses');
  return response.data;
};

export const deletePmsVerificationStatus = async (id: number) => {
  await api.delete(`/pms-verification-statuses/${id}`);
  mutate('/pms-verification-statuses');
};

export const createPmsDepartmentType = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-department-types', data);
  mutate('/pms-department-types');
  return response.data;
};

export const updatePmsDepartmentType = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-department-types/${id}`, data);
  mutate(`/pms-department-types/${id}`);
  mutate('/pms-department-types');
  return response.data;
};

export const deletePmsDepartmentType = async (id: number) => {
  await api.delete(`/pms-department-types/${id}`);
  mutate('/pms-department-types');
};

// --- PMS domain ---

export const createPmsDepartment = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-departments', data);
  mutate('/pms-departments');
  return response.data;
};

export const updatePmsDepartment = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-departments/${id}`, data);
  mutate(`/pms-departments/${id}`);
  mutate('/pms-departments');
  return response.data;
};

export const deletePmsDepartment = async (id: number) => {
  await api.delete(`/pms-departments/${id}`);
  mutate('/pms-departments');
};

export const createPmsEquipmentSystem = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-equipment-systems', data);
  mutate('/pms-equipment-systems');
  return response.data;
};

export const updatePmsEquipmentSystem = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-equipment-systems/${id}`, data);
  mutate(`/pms-equipment-systems/${id}`);
  mutate('/pms-equipment-systems');
  return response.data;
};

export const deletePmsEquipmentSystem = async (id: number) => {
  await api.delete(`/pms-equipment-systems/${id}`);
  mutate('/pms-equipment-systems');
};

export const createPmsComponent = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-components', data);
  mutate('/pms-components');
  return response.data;
};

export const updatePmsComponent = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-components/${id}`, data);
  mutate(`/pms-components/${id}`);
  mutate('/pms-components');
  return response.data;
};

export const deletePmsComponent = async (id: number) => {
  await api.delete(`/pms-components/${id}`);
  mutate('/pms-components');
};

export const createPmsSubComponent = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-sub-components', data);
  mutate('/pms-sub-components');
  return response.data;
};

export const updatePmsSubComponent = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-sub-components/${id}`, data);
  mutate(`/pms-sub-components/${id}`);
  mutate('/pms-sub-components');
  return response.data;
};

export const deletePmsSubComponent = async (id: number) => {
  await api.delete(`/pms-sub-components/${id}`);
  mutate('/pms-sub-components');
};

export const createPmsEquipment = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-equipment', data);
  mutate('/pms-equipment');
  return response.data;
};

export const updatePmsEquipment = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-equipment/${id}`, data);
  mutate(`/pms-equipment/${id}`);
  mutate('/pms-equipment');
  return response.data;
};

export const deletePmsEquipment = async (id: number) => {
  await api.delete(`/pms-equipment/${id}`);
  mutate('/pms-equipment');
};

export const createPmsEquipmentInstance = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-equipment-instances', data);
  mutate('/pms-equipment-instances');
  return response.data;
};

export const updatePmsEquipmentInstance = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-equipment-instances/${id}`, data);
  mutate(`/pms-equipment-instances/${id}`);
  mutate('/pms-equipment-instances');
  return response.data;
};

export const deletePmsEquipmentInstance = async (id: number) => {
  await api.delete(`/pms-equipment-instances/${id}`);
  mutate('/pms-equipment-instances');
};

export const createPmsMaintenanceJob = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-maintenance-jobs', data);
  mutate('/pms-maintenance-jobs');
  return response.data;
};

export const updatePmsMaintenanceJob = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-maintenance-jobs/${id}`, data);
  mutate(`/pms-maintenance-jobs/${id}`);
  mutate('/pms-maintenance-jobs');
  return response.data;
};

export const deletePmsMaintenanceJob = async (id: number) => {
  await api.delete(`/pms-maintenance-jobs/${id}`);
  mutate('/pms-maintenance-jobs');
};

export const createPmsMaintenanceCounter = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-maintenance-counters', data);
  mutate('/pms-maintenance-counters');
  return response.data;
};

export const updatePmsMaintenanceCounter = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-maintenance-counters/${id}`, data);
  mutate(`/pms-maintenance-counters/${id}`);
  mutate('/pms-maintenance-counters');
  return response.data;
};

export const deletePmsMaintenanceCounter = async (id: number) => {
  await api.delete(`/pms-maintenance-counters/${id}`);
  mutate('/pms-maintenance-counters');
};

export const createPmsWorkOrder = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-work-orders', data);
  mutate('/pms-work-orders');
  return response.data;
};

export const updatePmsWorkOrder = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-work-orders/${id}`, data);
  mutate(`/pms-work-orders/${id}`);
  mutate('/pms-work-orders');
  return response.data;
};

export const deletePmsWorkOrder = async (id: number) => {
  await api.delete(`/pms-work-orders/${id}`);
  mutate('/pms-work-orders');
};

export const createPmsDefect = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-defects', data);
  mutate('/pms-defects');
  return response.data;
};

export const updatePmsDefect = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-defects/${id}`, data);
  mutate(`/pms-defects/${id}`);
  mutate('/pms-defects');
  return response.data;
};

export const deletePmsDefect = async (id: number) => {
  await api.delete(`/pms-defects/${id}`);
  mutate('/pms-defects');
};

export const createNoonReport = async (data: Record<string, unknown>) => {
  const response = await api.post('/noon-reports', data);
  mutate('/noon-reports');
  return response.data;
};

export const updateNoonReport = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/noon-reports/${id}`, data);
  mutate(`/noon-reports/${id}`);
  mutate('/noon-reports');
  return response.data;
};

export const deleteNoonReport = async (id: number) => {
  await api.delete(`/noon-reports/${id}`);
  mutate('/noon-reports');
};

export const createPmsTool = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-tools', data);
  mutate('/pms-tools');
  return response.data;
};

export const updatePmsTool = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-tools/${id}`, data);
  mutate(`/pms-tools/${id}`);
  mutate('/pms-tools');
  return response.data;
};

export const deletePmsTool = async (id: number) => {
  await api.delete(`/pms-tools/${id}`);
  mutate('/pms-tools');
};

export const createPmsDocument = async (data: Record<string, unknown>) => {
  const response = await api.post('/pms-documents', data);
  mutate('/pms-documents');
  return response.data;
};

export const updatePmsDocument = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`/pms-documents/${id}`, data);
  mutate(`/pms-documents/${id}`);
  mutate('/pms-documents');
  return response.data;
};

export const deletePmsDocument = async (id: number) => {
  await api.delete(`/pms-documents/${id}`);
  mutate('/pms-documents');
};

// --- E-Forms ---
const E_FORM_DEFINITIONS = '/e-form-definitions';
const E_FORM_SUBMISSIONS = '/e-form-submissions';

export const createEFormDefinition = async (data: Record<string, unknown>) => {
  const response = await api.post(E_FORM_DEFINITIONS, data);
  mutate((key) => typeof key === 'string' && key.startsWith(E_FORM_DEFINITIONS));
  return response.data;
};

export const updateEFormDefinition = async (id: number, data: Record<string, unknown>) => {
  const response = await api.patch(`${E_FORM_DEFINITIONS}/${id}`, data);
  mutate(`${E_FORM_DEFINITIONS}/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith(E_FORM_DEFINITIONS));
  return response.data;
};

export const deleteEFormDefinition = async (id: number) => {
  await api.delete(`${E_FORM_DEFINITIONS}/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith(E_FORM_DEFINITIONS));
};

export const saveEFormDraftSchema = async (id: number, schemaJson: unknown) => {
  const response = await api.patch(`${E_FORM_DEFINITIONS}/${id}/draft`, { schemaJson });
  mutate(`${E_FORM_DEFINITIONS}/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith(E_FORM_DEFINITIONS));
  return response.data;
};

export const publishEFormDefinition = async (id: number) => {
  const response = await api.post(`${E_FORM_DEFINITIONS}/${id}/publish`);
  mutate(`${E_FORM_DEFINITIONS}/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith(E_FORM_DEFINITIONS));
  return response.data;
};

export const unpublishEFormDefinition = async (id: number) => {
  const response = await api.post(`${E_FORM_DEFINITIONS}/${id}/unpublish`);
  mutate(`${E_FORM_DEFINITIONS}/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith(E_FORM_DEFINITIONS));
  return response.data;
};

export const cloneEFormDefinition = async (id: number) => {
  const response = await api.post(`${E_FORM_DEFINITIONS}/${id}/clone`);
  mutate((key) => typeof key === 'string' && key.startsWith(E_FORM_DEFINITIONS));
  return response.data;
};

export const restoreEFormVersion = async (id: number, versionId: number) => {
  const response = await api.post(`${E_FORM_DEFINITIONS}/${id}/versions/${versionId}/restore`);
  mutate(`${E_FORM_DEFINITIONS}/${id}`);
  mutate((key) => typeof key === 'string' && key.startsWith(E_FORM_DEFINITIONS));
  return response.data;
};

export type EFormImportResult = {
  definition: {
    id: number;
    code: string;
    name: string;
    description?: string | null;
    isActive: boolean;
    sourceFileName?: string | null;
    sourceFileMime?: string | null;
    sourceFileSize?: number | null;
    versions?: Array<{
      id: number;
      definitionId: number;
      version: number;
      status: string;
      schemaJson: unknown;
    }>;
  };
  /** Generated draft schema (preferred over reading versions[]). */
  schema?: unknown;
  warnings: string[];
  extraction: 'text' | 'docx' | 'doc' | 'vision';
  confidence: number | null;
};

const EFORM_IMPORT_TIMEOUT_MS = 180_000;

export const importEFormFromFile = async (file: File, definitionId?: number): Promise<EFormImportResult> => {
  const formData = new FormData();
  formData.append('file', file);
  const url =
    definitionId != null
      ? `${E_FORM_DEFINITIONS}/${definitionId}/import-from-file`
      : `${E_FORM_DEFINITIONS}/import-from-file`;
  const response = await api.post<EFormImportResult>(url, formData, {
    timeout: EFORM_IMPORT_TIMEOUT_MS,
    skipGlobalErrorToast: true,
  });
  mutate((key) => typeof key === 'string' && key.startsWith(E_FORM_DEFINITIONS));
  if (definitionId != null) mutate(`${E_FORM_DEFINITIONS}/${definitionId}`);
  return response.data;
};

function filenameFromContentDisposition(cd: unknown, fallback: string): string {
  if (cd && typeof cd === 'string') {
    const m = /filename="?([^";]+)"?/i.exec(cd);
    if (m?.[1]) return m[1];
  }
  return fallback;
}

function triggerBlobDownload(data: Blob, filename: string) {
  const url = window.URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

export const downloadEFormSourceFile = async (definitionId: number) => {
  const response = await api.get(`${E_FORM_DEFINITIONS}/${definitionId}/source-file`, {
    responseType: 'blob',
  });
  triggerBlobDownload(
    response.data,
    filenameFromContentDisposition(response.headers['content-disposition'], `eform-${definitionId}-template`),
  );
};

export const exportEFormSubmissionTemplate = async (
  submissionId: number,
): Promise<{ filled: boolean }> => {
  const response = await api.get(`${E_FORM_SUBMISSIONS}/${submissionId}/export-template`, {
    responseType: 'blob',
  });
  const filledRaw =
    response.headers['x-eform-template-filled'] ?? response.headers['X-Eform-Template-Filled'];
  const filled = filledRaw === '1' || String(filledRaw).toLowerCase() === 'true';
  triggerBlobDownload(
    response.data,
    filenameFromContentDisposition(
      response.headers['content-disposition'],
      `eform-submission-${submissionId}`,
    ),
  );
  return { filled };
};

export const exportEFormSubmissionLayout = async (
  submissionId: number,
  templateId?: string,
) => {
  const response = await api.get(`${E_FORM_SUBMISSIONS}/${submissionId}/export-layout`, {
    responseType: 'blob',
    params: templateId ? { templateId } : undefined,
  });
  triggerBlobDownload(
    response.data,
    filenameFromContentDisposition(
      response.headers['content-disposition'],
      `eform-submission-${submissionId}-layout.pdf`,
    ),
  );
};

export const exportEFormTableLayout = async (definitionId: number, templateId?: string) => {
  const response = await api.get(`${E_FORM_DEFINITIONS}/${definitionId}/export-table`, {
    responseType: 'blob',
    params: templateId ? { templateId } : undefined,
  });
  triggerBlobDownload(
    response.data,
    filenameFromContentDisposition(
      response.headers['content-disposition'],
      `eform-${definitionId}-table.pdf`,
    ),
  );
};

export const createEFormSubmission = async (data: {
  definitionId: number;
  values?: Record<string, unknown>;
  vesselId?: number;
}) => {
  const response = await api.post(E_FORM_SUBMISSIONS, data);
  revalidateEFormSubmissions(response.data?.id);
  return response.data;
};

export const updateEFormSubmission = async (id: number, values: Record<string, unknown>) => {
  const response = await api.patch(`${E_FORM_SUBMISSIONS}/${id}`, { values });
  await mutate(`${E_FORM_SUBMISSIONS}/${id}`, response.data, { revalidate: true });
  revalidateEFormSubmissions(id);
  return response.data;
};

export const submitEFormSubmission = async (id: number) => {
  const response = await api.post(`${E_FORM_SUBMISSIONS}/${id}/submit`);
  await mutate(`${E_FORM_SUBMISSIONS}/${id}`, response.data, { revalidate: true });
  revalidateEFormSubmissions(id);
  // Submit may create/submit an approval request — refresh Approvals inbox/list too.
  revalidateApprovals(response.data?.approval?.id);
  return response.data;
};

export const revertEFormSubmissionToDraft = async (id: number) => {
  const response = await api.post(`${E_FORM_SUBMISSIONS}/${id}/revert-to-draft`);
  await mutate(`${E_FORM_SUBMISSIONS}/${id}`, response.data, { revalidate: true });
  revalidateEFormSubmissions(id);
  revalidateApprovals();
  return response.data;
};

export const deleteEFormSubmission = async (id: number) => {
  await api.delete(`${E_FORM_SUBMISSIONS}/${id}`);
  revalidateEFormSubmissions(id);
  revalidateApprovals();
};

export const clearEFormSubmissions = async (definitionId: number) => {
  const response = await api.delete(`${E_FORM_DEFINITIONS}/${definitionId}/submissions`);
  revalidateEFormSubmissions();
  mutate(`${E_FORM_DEFINITIONS}/${definitionId}`);
  return response.data as { message: string; count: number };
};

export type EFormUploadedFile = {
  storageKey: string;
  fileName: string;
  mimeType: string;
  size: number;
};

export const uploadEFormFiles = async (files: File[]): Promise<EFormUploadedFile[]> => {
  const formData = new FormData();
  for (const file of files) {
    formData.append('files', file);
  }
  const response = await api.post('/e-form-files', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const eFormFileDownloadPath = (storageKey: string): string =>
  `/e-form-files/${encodeURIComponent(storageKey)}`;

export const eFormFileDownloadUrl = (storageKey: string): string => {
  const base = (api.defaults.baseURL || '').replace(/\/$/, '');
  return `${base}${eFormFileDownloadPath(storageKey)}`;
};

// --- E-Form reports ---

export const queryEFormReport = async (definitionId: number, query: Record<string, unknown>) => {
  const response = await api.post(`${E_FORM_DEFINITIONS}/${definitionId}/reports/query`, query);
  return response.data;
};

export const drilldownEFormReport = async (
  definitionId: number,
  body: Record<string, unknown>,
) => {
  const response = await api.post(`${E_FORM_DEFINITIONS}/${definitionId}/reports/drilldown`, body);
  return response.data as { ids: number[]; total: number };
};

export const createEFormSavedReport = async (
  definitionId: number,
  data: Record<string, unknown>,
) => {
  const response = await api.post(`${E_FORM_DEFINITIONS}/${definitionId}/reports/saved`, data);
  mutate(`${E_FORM_DEFINITIONS}/${definitionId}/reports/saved`);
  mutate('/e-form-reports/sidebar');
  return response.data;
};

export const updateEFormSavedReport = async (
  definitionId: number,
  reportId: number,
  data: Record<string, unknown>,
) => {
  const response = await api.patch(
    `${E_FORM_DEFINITIONS}/${definitionId}/reports/saved/${reportId}`,
    data,
  );
  mutate(`${E_FORM_DEFINITIONS}/${definitionId}/reports/saved`);
  mutate('/e-form-reports/sidebar');
  return response.data;
};

export const deleteEFormSavedReport = async (definitionId: number, reportId: number) => {
  await api.delete(`${E_FORM_DEFINITIONS}/${definitionId}/reports/saved/${reportId}`);
  mutate(`${E_FORM_DEFINITIONS}/${definitionId}/reports/saved`);
  mutate('/e-form-reports/sidebar');
};

export const saveEFormPrintLayout = async (
  definitionId: number,
  layoutJson: Record<string, unknown>,
) => {
  const response = await api.patch(`${E_FORM_DEFINITIONS}/${definitionId}/print-layout`, {
    layoutJson,
  });
  mutate(`${E_FORM_DEFINITIONS}/${definitionId}`);
  mutate(`${E_FORM_DEFINITIONS}/${definitionId}/print-layout`);
  return response.data;
};

export const createEFormRelation = async (
  definitionId: number,
  data: Record<string, unknown>,
) => {
  const response = await api.post(`${E_FORM_DEFINITIONS}/${definitionId}/relations`, data);
  mutate(`${E_FORM_DEFINITIONS}/${definitionId}/relations`);
  mutate(`${E_FORM_DEFINITIONS}/${definitionId}/reportable-fields`);
  return response.data;
};

export const deleteEFormRelation = async (definitionId: number, relationId: number) => {
  await api.delete(`${E_FORM_DEFINITIONS}/${definitionId}/relations/${relationId}`);
  mutate(`${E_FORM_DEFINITIONS}/${definitionId}/relations`);
  mutate(`${E_FORM_DEFINITIONS}/${definitionId}/reportable-fields`);
};

export const saveEFormReportSchedule = async (
  reportId: number,
  data: Record<string, unknown>,
  scheduleId?: number,
) => {
  const url = `/e-form-reports/${reportId}/schedules${scheduleId ? `/${scheduleId}` : ''}`;
  const response = scheduleId ? await api.patch(url, data) : await api.post(url, data);
  mutate(`/e-form-reports/${reportId}/schedules`);
  return response.data;
};

export const deleteEFormReportSchedule = async (reportId: number, scheduleId: number) => {
  await api.delete(`/e-form-reports/${reportId}/schedules/${scheduleId}`);
  mutate(`/e-form-reports/${reportId}/schedules`);
};

export const runEFormReportSchedule = async (reportId: number, scheduleId: number) => {
  const response = await api.post(`/e-form-reports/${reportId}/schedules/${scheduleId}/run`);
  mutate(`/e-form-reports/${reportId}/schedules`);
  return response.data;
};

export const downloadEFormReportScheduleArtifact = async (
  reportId: number,
  scheduleId: number,
) => {
  const response = await api.get(
    `/e-form-reports/${reportId}/schedules/${scheduleId}/artifact`,
    { responseType: 'blob' },
  );
  triggerBlobDownload(
    response.data,
    filenameFromContentDisposition(
      response.headers['content-disposition'],
      `e-form-report-${reportId}`,
    ),
  );
};

export const saveEFormAppPage = async (
  data: Record<string, unknown>,
  pageId?: number,
) => {
  const response = pageId
    ? await api.patch(`/e-form-app-pages/${pageId}`, data)
    : await api.post('/e-form-app-pages', data);
  mutate((key) => typeof key === 'string' && key.startsWith('/e-form-app-pages'));
  return response.data;
};

export const deleteEFormAppPage = async (pageId: number) => {
  await api.delete(`/e-form-app-pages/${pageId}`);
  mutate((key) => typeof key === 'string' && key.startsWith('/e-form-app-pages'));
};
